import React, { useState, useEffect, FC, useCallback, useRef, useMemo, Fragment } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { useNavigate, useParams, useOutletContext, useBlocker, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useAuth } from "@/app/providers/AuthProvider";
import Button from "@/components/Button";
import { Card, CardContent, CardHeader } from "@/components/Card";
import { cn } from "@/utils/cn";
import Input from "@/components/Input";
import Label from "@/components/Label";
import { CustomSelect } from "@/components/CustomSelect";

import toast from "react-hot-toast";
import { useUploadTemplateMedia, useCreateTemplate, useTemplate, useUpdateTemplate, useTemplates } from "../../chatbots/hooks/useWhatsAppTemplatesQuery";
import { AddButtonDropdown } from "../components/AddButtonDropdown";
import { AddIndustryModal } from "../components/AddIndustryModal";
import { AddSubcategoryModal } from "../components/AddSubcategoryModal";
import { Loader2, MessageSquare, Link, Phone, Copy, Smile, Bold, Italic, Strikethrough, Code, FileText, LayoutTemplate, AlignLeft, Type, MousePointer2, Eye, Info, AlertCircle, CloudUpload, TriangleAlert, Video, Check, Image, GripVertical, GripHorizontal, Trash2, Plus } from "lucide-react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import FormField from "@/components/FormField";
import { MediaLibrary } from "../../media/components/MediaLibrary";
import { Portal } from "@/components/Portal";
import { MediaType } from "../../media/types/media.types";
import WhatsAppPreview, {
    FormValues,
    Category,
    HeaderType,
    HeaderState,
    ButtonType,
    ButtonItem,

} from "../../chatbots/components/WhatsAppPreview";
import { Modal } from "@/components";
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/Tooltip';
import { AUTH_BODY_TRANSLATIONS } from "../utils/language";
import { inferVariableTypeFromTemplate } from "../utils/transformTemplate";
import emojiRegex from 'emoji-regex';
import { useTemplateLibraryFiltersQuery } from "../hooks/useTemplateLibraryQuery";
// ─── Types ────────────────────────────────────────────────────────────────────
type LockedType = "otp" | null;

interface Language { code: string; label: string; }

// ─── Text Helpers ─────────────────────────────────────────────────────────────
function extractVars(text = ""): number[] {
    const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
    return [...new Set(matches.map((m) => parseInt(m[1])))].sort((a, b) => a - b);
}
function extractNamedVars(text = ""): string[] {
    const matches = [...text.matchAll(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g)];
    return [...new Set(matches.map((m) => m[1]))];
}
function countPositionalVars(text = ""): number {
    const matches = text.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
}
function countNamedVars(text = ""): number {
    const matches = text.match(/\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g);
    return matches ? matches.length : 0;
}
function isSequential(vars: number[]): boolean {
    if (vars.length === 0) return true;
    for (let i = 0; i < vars.length; i++) if (vars[i] !== i + 1) return false;
    return true;
}
function truncateFileName(fileName: string, maxLen: number = 35): string {
    if (!fileName || fileName.length <= maxLen) return fileName;
    const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;

    const availableForName = maxLen - (extension ? extension.length + 4 : 3);
    if (availableForName <= 0) return fileName.substring(0, maxLen - 3) + "...";

    const prefixLen = Math.ceil(availableForName / 2);
    const suffixLen = Math.floor(availableForName / 2);

    const prefix = nameWithoutExt.substring(0, prefixLen);
    const suffix = nameWithoutExt.substring(nameWithoutExt.length - suffixLen);

    return `${prefix}...${suffix}${extension ? '.' + extension : ''}`;
}
function startsWithVar(text: string) { return /^\s*\{\{[^}]+\}\}/.test(text.trim()); }
function endsWithVar(text: string) { return /\{\{[^}]+\}\}\s*$/.test(text.trim()); }
function hasAdjacentVars(text: string) { return /\{\{[^}]+\}\}\s*\{\{[^}]+\}\}/.test(text); }

function countEmojis(text = "") {
    const regex = emojiRegex();
    return (text.match(regex) || []).length;
}
function insertAtCursor(original: string, insertion: string, pos: number) {
    return original.slice(0, pos) + insertion + original.slice(pos);
}
function formatFileSize(bytes: number) {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "kB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function normalizeButtonType(type: string | undefined): ButtonType {
    const normalized = type?.toLowerCase();
    if (normalized === "phone" || normalized === "phone_number") return "phone_number";
    if (normalized === "whatsapp_call" || normalized === "voice_call") return "voice_call";
    if (normalized === "quick_reply" || normalized === "url" || normalized === "otp" || normalized === "copy_code" || normalized === "flow") {
        return normalized;
    }
    return "quick_reply";
}

// ─── Yup Schema (ALL validation — errors only) ───────────────────────────────
// Each .test() may validate multiple rules and returns a joined error message
// using Yup.ValidationError so all messages are preserved.
const buildSchema = (): Yup.ObjectSchema<any> =>
    Yup.object({
        // ── Template Name ───────────────────────────────────────────────────
        templateName: Yup.string()
            .required("Template name is required")
            .test("meta-name-rules", "", function (value) {
                const errs: string[] = [];
                if (!value) return true; // required handles empty
                if (value.length > 512) errs.push("Max 512 characters allowed");
                if (!/^[a-z0-9_]+$/.test(value)) errs.push("Only lowercase letters, numbers, underscores allowed");
                if (value.startsWith("_") || value.endsWith("_")) errs.push("Cannot start or end with underscore");
                const existingTemplateNames = (this.options.context as any)?.existingTemplateNames as Set<string> | undefined;
                if (existingTemplateNames && value && existingTemplateNames.has(value.toLowerCase())) {
                    errs.push("Template name already exists. Please choose a different name.");
                }
                if (!errs.length) return true;
                return this.createError({ message: errs.join(" | ") });
            }),

        category: Yup.string().oneOf(["UTILITY", "MARKETING", "AUTHENTICATION"]).required(),
        language: Yup.string().required("Language is required"),
        variableType: Yup.string().oneOf(["positional", "named"]).required(),

        // ── Header ──────────────────────────────────────────────────────────
        header: Yup.object({
            text: Yup.string().test("meta-header-rules", "", function (value) {
                const root = this.options.context as FormValues | undefined;
                const { type } = (this.parent as HeaderState);
                if (type !== "TEXT" || !value) return true;
                const variableType = root?.variableType ?? "positional";
                const errs: string[] = [];

                if (value.length > 60) errs.push("Header max 60 characters");

                const positionalVars = extractVars(value);
                const namedVars = extractNamedVars(value);

                if (variableType === "positional" && namedVars.length > 0)
                    errs.push(`Check your variable type {{${namedVars.join("}}, {{")}}`);
                if (variableType === "named" && positionalVars.length > 0)
                    errs.push("Positional variables not allowed in Named mode. Use names like {{customer_name}}");

                const totalVars = variableType === "positional"
                    ? countPositionalVars(value)
                    : countNamedVars(value);
                if (totalVars > 1) errs.push("Header allows max 1 variable");

                if (variableType === "positional" && positionalVars.length > 0 && !isSequential(positionalVars))
                    errs.push("Variables must be sequential: {{1}}, {{2}}...");

                if (/\t/.test(value)) errs.push("Tab characters not allowed");
                if (/[~*_`]/.test(value)) errs.push("Formatting (*bold*, _italic_, ~strike~, `code`) not allowed in header");
                if (/\{\{[^}]*[!@#$%^&()+=[\];':"\\|,.<>/?`~]/.test(value)) errs.push("Special characters not allowed in header");

                // ✅ Allowlist check — blocks emojis, ZWJ sequences, variation selectors, and all extended Unicode
                if (/[^\u0020-\u007E\u00A0-\u00FF]/.test(value))
                    errs.push("Only standard characters allowed in header (no emojis or special Unicode)");

                // Explicit emoji check for a clearer error message (redundant but user-friendly)
                if (countEmojis(value) > 0) errs.push("Emojis not allowed in header (Meta guidelines)");

                if (!errs.length) return true;
                return this.createError({ message: errs.join(" | ") });
            }),
            mediaExample: Yup.string().test("media-handle-required", "Example media handle required for API submission", function (value) {
                const { type } = this.parent as HeaderState;
                if (type !== "TEXT" && type !== "LOCATION" && !value) return false;
                return true;
            }),
            example: Yup.string().test("header-example-required", "Example value required for header variable", function (value) {
                const { type, text } = this.parent as HeaderState;
                if (type !== "TEXT") return true;
                const hasVars = extractVars(text).length > 0 || extractNamedVars(text).length > 0;
                if (hasVars && !value?.trim()) return false;
                if (value && value.length > 256) return this.createError({ message: "Header example max 256 characters" });
                return true;
            }),
        }),

        // ── Body ────────────────────────────────────────────────────────────
        body: Yup.object({
            text: Yup.string()
                .required("Body text is required")
                .test("meta-body-rules", "", function (value) {
                    const root = this.options.context as FormValues | undefined;
                    const category: Category = root?.category ?? "UTILITY";
                    const variableType = root?.variableType ?? "positional";
                    if (!value) return true; // required handles empty
                    const errs: string[] = [];
                    if (value.length > 1024) errs.push("Body max 1024 characters");
                    const positionalVars = extractVars(value);
                    const namedVars = extractNamedVars(value);
                    if (variableType === "positional" && namedVars.length > 0)
                        errs.push(`Check your variable type {{${namedVars.join("}}, {{")}}}`);
                    if (variableType === "named" && positionalVars.length > 0)
                        errs.push("Positional variables ({{1}}, {{2}}) not allowed in Named mode. Use {{customer_name}} instead");
                    if (variableType === "positional" && !isSequential(positionalVars))
                        errs.push("Variables must be sequential {{1}}, {{2}}, {{3}}...");
                    if (startsWithVar(value) && category !== "AUTHENTICATION")
                        errs.push("Body cannot start with a variable");
                    if (endsWithVar(value)) errs.push("Body cannot end with a variable");
                    if (hasAdjacentVars(value)) errs.push("Variables cannot be adjacent {{1}}{{2}}");
                    if (/\t/.test(value)) errs.push("Tab characters not allowed");
                    if (/ {5,}/.test(value)) errs.push("Max 4 consecutive spaces allowed");
                    if (/\n{3,}/.test(value)) errs.push("Max 2 consecutive empty lines allowed");
                    if (/\{\{[^}]*[!@#$%^&()+=[\];':"\\|,.<>/?`~]/.test(value)) errs.push("Special characters not allowed in variable");

                    if (category === "AUTHENTICATION") {
                        const vars = variableType === "positional" ? positionalVars : namedVars;
                        if (vars.length > 1) errs.push("Authentication templates allow only 1 variable");
                    }
                    const vars = variableType === "positional" ? positionalVars : namedVars;
                    if (vars.length > 0) {
                        const varPattern = variableType === "positional" ? /\{\{\d+\}\}/g : /\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g;
                        const textWithoutVars = value.replace(varPattern, "");
                        if (textWithoutVars.trim().length / vars.length < 3)
                            errs.push("Too many variables for text length. Ensure at least 3 characters per variable.");
                    }
                    if (!errs.length) return true;
                    return this.createError({ message: errs.join(" | ") });
                }),

            examples: Yup.array().test("meta-body-examples", "", function (examples) {
                const root = this.options.context as FormValues | undefined;
                const category = root?.category ?? "UTILITY";
                const bodyText = root?.body?.text ?? "";
                const variableType = root?.variableType ?? "positional";
                if (category === "AUTHENTICATION") return true;
                const errs: string[] = [];

                if (variableType === "positional") {
                    extractVars(bodyText).forEach((v) => {
                        const ex = (examples as string[])?.[v - 1];
                        if (!ex || !String(ex).trim())
                            errs.push(`Example value required for {{${v}}}`);
                        else if (String(ex).length > 256)
                            errs.push(`Example for {{${v}}} too long (max 256 characters) — Meta may reject`);
                    });
                } else {
                    const namedVars = extractNamedVars(bodyText);
                    namedVars.forEach((v) => {
                        const index = namedVars.indexOf(v);
                        const ex = (examples as string[])?.[index];
                        if (!ex || !String(ex).trim())
                            errs.push(`Example value required for {{${v}}}`);
                        else if (String(ex).length > 256)
                            errs.push(`Example for {{${v}}} too long (max 256 characters) — Meta may reject`);
                    });
                }

                if (!errs.length) return true;
                return this.createError({ message: errs.join(" | ") });
            }),
        }),

        // ── Footer ──────────────────────────────────────────────────────────
        footer: Yup.object({
            text: Yup.string().test("meta-footer-rules", "", function (value) {
                if (!value) return true;
                const errs: string[] = [];
                if (value.length > 60) errs.push("Footer max 60 characters");
                if (extractVars(value).length > 0) errs.push("Variables not allowed in footer");
                if (/https?:\/\//.test(value)) errs.push("URLs not allowed in footer");
                if (countEmojis(value) > 0) errs.push("Emojis not allowed in footer");
                if (!errs.length) return true;
                return this.createError({ message: errs.join(" | ") });
            }),
            codeExpirationMinutes: Yup.number()
                .nullable()
                .min(1, "Minimum 1 minute")
                .max(90, "Maximum 90 minutes"),
        }),

        // ── Buttons (array-level) ────────────────────────────────────────────
        buttons: Yup.array()
            .test("meta-buttons-rules", "", function (buttons) {
                const root = this.options.context as FormValues | undefined;
                const category: Category = root?.category ?? "UTILITY";
                const btns = (buttons as ButtonItem[]) ?? [];
                const errs: string[] = [];
                if (btns.length > 10) errs.push("Maximum 10 buttons allowed");
                const qr = btns.filter((b) => b.type === "quick_reply");
                const url = btns.filter((b) => b.type === "url");
                const phone = btns.filter((b) => b.type === "phone_number");
                const otp = btns.filter((b) => b.type === "otp");
                const copyCode = btns.filter((b) => b.type === "copy_code");
                const flow = btns.filter((b) => b.type === "flow");
                const voiceCall = btns.filter((b) => b.type === "voice_call");
                if (qr.length > 10) errs.push("Max 10 Quick Reply buttons");
                if (url.length > 2) errs.push("Max 2 URL buttons allowed");
                if (phone.length > 1) errs.push("Max 1 Phone button allowed");
                if (voiceCall.length > 1) errs.push("Max 1 voice_call button allowed");
                if (phone.length > 0 && voiceCall.length > 0) errs.push("Phone Number and Voice Call buttons cannot be used together");
                if (otp.length > 0) {
                    if (otp.length > 1) errs.push("Only 1 OTP button allowed");
                    if (btns.length > 1) errs.push("OTP buttons cannot be combined with other buttons");
                    if (category !== "AUTHENTICATION") errs.push("OTP buttons only allowed in AUTHENTICATION templates");
                }
                if (copyCode.length > 1) errs.push("Max 1 copy_code button allowed");
                if (flow.length > 1) errs.push("Max 1 FLOW button allowed");
                if (voiceCall.length > 1) errs.push("Max 1 voice_call button allowed");
                // Unique button labels (ignore types without a visible label)
                const labelCounts: Record<string, number> = {};
                btns.forEach((b) => {
                    let label = "";
                    if (b.type === "voice_call") label = (b as any).voiceCallDisplayText || b.text || "";
                    else if (b.type !== "otp" && b.type !== "copy_code") label = b.text || "";
                    const key = label.trim().toLowerCase();
                    if (!key) return;
                    labelCounts[key] = (labelCounts[key] || 0) + 1;
                });
                const dupLabels = Object.entries(labelCounts)
                    .filter(([, count]) => count > 1)
                    .map(([label]) => label);
                if (dupLabels.length > 0) {
                    errs.push("Button text must be unique");
                }
                if (qr.length > 0 && qr.length < btns.length) {
                    const isQr = btns.map((b) => b.type === "quick_reply");
                    let switches = 0;
                    for (let i = 1; i < isQr.length; i++) {
                        if (isQr[i] !== isQr[i - 1]) switches += 1;
                        if (switches > 1) break;
                    }
                    if (switches > 1) {
                        errs.push("Quick Reply buttons must be grouped together (all quick replies on one side). Example: Quick Reply, Quick Reply, URL, Phone OR URL, Phone, Quick Reply, Quick Reply");
                    }
                }
                if (category === "AUTHENTICATION") {
                    if (btns.length > 0 && otp.length === 0)
                        errs.push("AUTHENTICATION templates require an OTP button");
                    if (qr.length > 0 || url.length > 0 || phone.length > 0 || copyCode.length > 0 || flow.length > 0 || voiceCall.length > 0)
                        errs.push("AUTHENTICATION templates can only have OTP buttons");
                }
                if (!errs.length) return true;
                return this.createError({ message: errs.join(" | ") });
            })
            .of(
                // ── Per-button rules ──────────────────────────────────────────
                Yup.object().test("meta-button-item-rules", "", function (btn) {
                    if (!btn) return true;
                    const b = btn as ButtonItem;
                    const errs: string[] = [];

                    if (b.type === "otp") {
                        if (!b.otpType) errs.push("OTP type required (copy_code or ONE_TAP)");
                    } else if (b.type !== "copy_code" && b.type !== "voice_call") {
                        if (!b.text) errs.push("Button label required");
                        else if (b.text.length > 25) errs.push("Button label max 25 characters");
                    }

                    if (b.type === "url") {
                        if (!b.url) {
                            errs.push("URL required for CTA button");
                        } else {
                            if (!b.url.startsWith("https://")) errs.push("URL must start with https://");
                            if (b.url.length > 2000) errs.push("URL max 2000 characters");
                            try { new URL(b.url.replace(/\{\{\d+\}\}/g, "test")); }
                            catch { errs.push("Invalid URL format — Meta requires valid, publicly accessible URLs"); }
                            if (b.url.includes("wa.me") || b.url.includes("whatsapp.com"))
                                errs.push("WhatsApp links (wa.me) not allowed in CTA buttons");
                            const urlVars = extractVars(b.url);
                            if (urlVars.length > 1) errs.push("URL button allows only 1 variable");
                            if (urlVars.length === 1 && !b.urlExample?.trim())
                                errs.push("URL variable requires example value");
                            else if (urlVars.length === 1 && b.urlExample && b.urlExample.length > 256)
                                errs.push("URL example too long (max 256 characters)");
                        }
                    }

                    if (b.type === "phone_number") {
                        if (!b.phone) errs.push("Phone number required");
                        else if (!b.phone.startsWith("+")) errs.push("Phone must start with + (international format)");
                    }

                    if (b.type === "quick_reply" && extractVars(b.text).length > 0)
                        errs.push("Quick Reply buttons cannot contain variables");

                    if (b.type === "copy_code") {
                        if (!b.copyCodeExample) errs.push("Copy code example required (4-15 characters)");
                        else if (b.copyCodeExample.length < 4 || b.copyCodeExample.length > 15)
                            errs.push("Copy code must be 4-15 characters");
                    }

                    if (b.type === "flow") {
                        if (!b.flowId) errs.push("Flow ID required");
                        else if (!/^\d{16}$/.test(b.flowId)) errs.push("Flow ID must be 16 digits");
                    }

                    if (b.type === "voice_call") {
                        if (!b.voiceCallDisplayText) errs.push("Display text required for voice call button");
                        else if (b.voiceCallDisplayText.length > 20) errs.push("Display text max 20 characters");
                        if (b.voiceCallTtl !== undefined && (b.voiceCallTtl < 1440 || b.voiceCallTtl > 43200))
                            errs.push("Duration must be between 1 and 30 days");
                    }

                    if (!errs.length) return true;
                    return this.createError({ message: errs.join(" | ") });
                })
            ),
    });

// Yup stores joined messages as "msg1 | msg2 | msg3" — split them for display
function splitErrs(msg: string | undefined): string[] {
    if (!msg) return [];
    return msg.split(" | ").map((s) => s.trim()).filter(Boolean);
}


// ─── JSON Generator (unchanged) ──────────────────────────────────────────────
function generateJSON(
    s: FormValues,
    libraryMeta?: {
        templateLibraryIndustryId?: string;
        templateLibrarySubcategoryId?: string;
        libraryPreviewContent?: string;
    }
): object {
    const components: any[] = [];
    let messageSendTtlSeconds: number | undefined;

    if (s.category === "AUTHENTICATION") {

        const bodyText = s.footer.addSecurityRecommendation
            ? getAuthBodyText(s.language, true)
            : s.body.text;
        const bodyComp: any = { type: "body", text: s.category !== "AUTHENTICATION" ? bodyText : undefined };
        if (s.footer.addSecurityRecommendation) bodyComp.add_security_recommendation = true;
        components.push(bodyComp);
        if (s.footer.codeExpirationMinutes)
            components.push({ type: "footer", code_expiration_minutes: s.footer.codeExpirationMinutes });
        if (s.footer.codeExpirationMinutes)
            messageSendTtlSeconds = Number(s.footer.codeExpirationMinutes) * 60;
        const otpButton = s.buttons.find((b) => b.type === "otp");
        if (otpButton) {
            const btnObj: any = { type: "otp", otp_type: otpButton.otpType || "copy_code" };
            if (otpButton.text) btnObj.text = otpButton.text;
            components.push({ type: "buttons", buttons: [btnObj] });
        } else {
            components.push({ type: "buttons", buttons: [{ type: "otp", otp_type: "copy_code" }] });
        }
    } else {
        // Only add header component if it's enabled and has content
        if (s.header.type === "TEXT" && s.header.text) {
            const comp: any = { type: "header", format: "text", text: s.header.text };
            const headerVars = extractVars(s.header.text);
            const headerNamedVars = extractNamedVars(s.header.text);
            if ((headerVars.length > 0 || headerNamedVars.length > 0) && s.header.example) {
                if (s.variableType === "positional") {
                    comp.example = { header_text: [s.header.example] };
                } else {
                    comp.example = { header_text_named_params: headerNamedVars.map((v) => ({ param_name: v, example: s.header.example })) };
                }
            }
            components.push(comp);
        } else if (s.header.type === "LOCATION") {
            components.push({ type: "header", format: "location" });
        } else if (s.header.type !== "TEXT" && s.header.mediaExample) {
            const comp: any = { type: "header", format: s.header.type.toLowerCase() };
            comp.example = { header_handle: [s.header.mediaExample] };
            components.push(comp);
        }
        const bodyComp: any = { type: "body", text: s.body.text };
        const bodyVars = extractVars(s.body.text);
        const bodyNamedVars = extractNamedVars(s.body.text);
        if (s.variableType === "positional" && bodyVars.length > 0)
            bodyComp.example = { body_text: [bodyVars.map((v) => s.body.examples[v - 1] || `value_${v}`)] };
        else if (s.variableType === "named" && bodyNamedVars.length > 0)
            bodyComp.example = {
                body_text_named_params: bodyNamedVars.map((v) => ({ param_name: v, example: s.body.examples[bodyNamedVars.indexOf(v)] || `value_${v}` })),
            };
        components.push(bodyComp);
        if (s.footer.text)
            components.push({ type: "footer", text: s.footer.text });
        if (s.buttons.length > 0) {
            const buttonsArray = s.buttons.map((b) => {
                if (b.type === "quick_reply") return { type: "quick_reply", text: b.text };
                if (b.type === "url") {
                    const btn: any = { type: "url", text: b.text, url: b.url };
                    if (extractVars(b.url || "").length > 0 && b.urlExample) {
                        if (s.variableType === "positional") btn.example = [b.urlExample];
                        else btn.example = extractNamedVars(b.url || "").map((v) => ({ param_name: v, example: b.urlExample }));
                    }
                    return btn;
                }
                if (b.type === "phone_number") return { type: "phone_number", text: b.text, phone_number: b.phone };
                if (b.type === "otp") return { type: "otp", otp_type: b.otpType || "copy_code" };
                if (b.type === "copy_code") return { type: "copy_code", example: b.copyCodeExample || "PROMO123" };
                if (b.type === "flow") return { type: "flow", text: b.text, flow_id: b.flowId || "" };
                if (b.type === "voice_call")
                    return { type: "voice_call", text: b.voiceCallDisplayText || b.text || "Call on WhatsApp" };

                return null;
            }).filter(Boolean);
            components.push({ type: "buttons", buttons: buttonsArray });
        }
    }
    const payload: any = {
        name: s.templateName,
        language: s.language,
        category: s.category.toLowerCase(),
        components
    };

    // Add mediaUrl for persistent internal reference if it exists
    if (s.header.type !== "TEXT" && s.header.type !== "LOCATION" && s.header.mediaPreview) {
        payload.mediaUrl = s.header.mediaPreview;
    }

    if (s.category !== "AUTHENTICATION") payload.parameter_format = s.variableType;
    if (messageSendTtlSeconds) payload.message_send_ttl_seconds = messageSendTtlSeconds;
    if (libraryMeta?.templateLibraryIndustryId) payload.templateLibraryIndustryId = libraryMeta.templateLibraryIndustryId;
    if (libraryMeta?.templateLibrarySubcategoryId) payload.templateLibrarySubcategoryId = libraryMeta.templateLibrarySubcategoryId;
    if (libraryMeta) payload.libraryPreviewContent = libraryMeta.libraryPreviewContent || s.body.text || "";
    return payload;
}

// ─── Media Type Config ─────────────────────────────────────────────────────────
const MEDIA_CONFIG: Record<HeaderType, { maxSize: number; accept: string; extensions: string[] }> = {
    IMAGE: { maxSize: 5 * 1024 * 1024, accept: "image/jpeg,image/jpg,image/png", extensions: ["jpg", "jpeg", "png"] },
    VIDEO: { maxSize: 16 * 1024 * 1024, accept: "video/mp4,video/3gpp", extensions: ["mp4", "3gp"] },
    DOCUMENT: { maxSize: 100 * 1024 * 1024, accept: "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation", extensions: ["pdf", "doc", "docx", "xlsx", "pptx"] },
    TEXT: { maxSize: 0, accept: "", extensions: [] },
    LOCATION: { maxSize: 0, accept: "", extensions: [] },
};

// ─── Constants ────────────────────────────────────────────────────────────────
const LANGUAGES: Language[] = [
    { code: "en", label: "English" }, { code: "en_GB", label: "English (UK)" }, { code: "en_US", label: "English (US)" },
    { code: "en_AE", label: "English (UAE)" }, { code: "en_AU", label: "English (AUS)" }, { code: "en_CA", label: "English (CAN)" },
    { code: "en_GH", label: "English (GHA)" }, { code: "en_IE", label: "English (IRL)" }, { code: "en_IN", label: "English (IND)" },
    { code: "en_JM", label: "English (JAM)" }, { code: "en_MY", label: "English (MYS)" }, { code: "en_NZ", label: "English (NZL)" },
    { code: "en_QA", label: "English (QAT)" }, { code: "en_SG", label: "English (SGP)" }, { code: "en_UG", label: "English (UGA)" },
    { code: "en_ZA", label: "English (ZAF)" }, { code: "af", label: "Afrikaans" }, { code: "sq", label: "Albanian" },
    { code: "ar", label: "Arabic" }, { code: "ar_EG", label: "Arabic (EGY)" }, { code: "ar_AE", label: "Arabic (UAE)" },
    { code: "ar_LB", label: "Arabic (LBN)" }, { code: "ar_MA", label: "Arabic (MAR)" }, { code: "ar_QA", label: "Arabic (QAT)" },
    { code: "az", label: "Azerbaijani" }, { code: "be_BY", label: "Belarusian" }, { code: "bn", label: "Bengali" },
    { code: "bn_IN", label: "Bengali (IND)" }, { code: "bg", label: "Bulgarian" }, { code: "ca", label: "Catalan" },
    { code: "zh_CN", label: "Chinese (CHN)" }, { code: "zh_HK", label: "Chinese (HKG)" }, { code: "zh_TW", label: "Chinese (TAI)" },
    { code: "hr", label: "Croatian" }, { code: "cs", label: "Czech" }, { code: "da", label: "Danish" },
    { code: "prs_AF", label: "Dari" }, { code: "nl", label: "Dutch" }, { code: "nl_BE", label: "Dutch (BEL)" },
    { code: "et", label: "Estonian" }, { code: "fil", label: "Filipino" }, { code: "fi", label: "Finnish" },
    { code: "fr", label: "French" }, { code: "fr_BE", label: "French (BEL)" }, { code: "fr_CA", label: "French (CAN)" },
    { code: "fr_CH", label: "French (CHE)" }, { code: "fr_CI", label: "French (CIV)" }, { code: "fr_MA", label: "French (MAR)" },
    { code: "ka", label: "Georgian" }, { code: "de", label: "German" }, { code: "de_AT", label: "German (AUT)" },
    { code: "de_CH", label: "German (CHE)" }, { code: "el", label: "Greek" }, { code: "gu", label: "Gujarati" },
    { code: "ha", label: "Hausa" }, { code: "he", label: "Hebrew" }, { code: "hi", label: "Hindi" },
    { code: "hu", label: "Hungarian" }, { code: "id", label: "Indonesian" }, { code: "ga", label: "Irish" },
    { code: "it", label: "Italian" }, { code: "ja", label: "Japanese" }, { code: "kn", label: "Kannada" },
    { code: "kk", label: "Kazakh" }, { code: "rw_RW", label: "Kinyarwanda" }, { code: "ko", label: "Korean" },
    { code: "ky_KG", label: "Kyrgyz (Kyrgyzstan)" }, { code: "lo", label: "Lao" }, { code: "lv", label: "Latvian" },
    { code: "lt", label: "Lithuanian" }, { code: "mk", label: "Macedonian" }, { code: "ms", label: "Malay" },
    { code: "ml", label: "Malayalam" }, { code: "mr", label: "Marathi" }, { code: "nb", label: "Norwegian" },
    { code: "ps_AF", label: "Pashto" }, { code: "fa", label: "Persian" }, { code: "pl", label: "Polish" },
    { code: "pt_BR", label: "Portuguese (BR)" }, { code: "pt_PT", label: "Portuguese (POR)" }, { code: "pa", label: "Punjabi" },
    { code: "ro", label: "Romanian" }, { code: "ru", label: "Russian" }, { code: "sr", label: "Serbian" },
    { code: "si_LK", label: "Sinhala" }, { code: "sk", label: "Slovak" }, { code: "sl", label: "Slovenian" },
    { code: "es", label: "Spanish" }, { code: "es_AR", label: "Spanish (ARG)" }, { code: "es_CL", label: "Spanish (CHL)" },
    { code: "es_CO", label: "Spanish (COL)" }, { code: "es_CR", label: "Spanish (CRI)" }, { code: "es_DO", label: "Spanish (DOM)" },
    { code: "es_EC", label: "Spanish (ECU)" }, { code: "es_HN", label: "Spanish (HND)" }, { code: "es_MX", label: "Spanish (MEX)" },
    { code: "es_PA", label: "Spanish (PAN)" }, { code: "es_PE", label: "Spanish (PER)" }, { code: "es_ES", label: "Spanish (SPA)" },
    { code: "es_UY", label: "Spanish (URY)" }, { code: "sw", label: "Swahili" }, { code: "sv", label: "Swedish" },
    { code: "ta", label: "Tamil" }, { code: "te", label: "Telugu" }, { code: "th", label: "Thai" },
    { code: "tr", label: "Turkish" }, { code: "uk", label: "Ukrainian" }, { code: "ur", label: "Urdu" },
    { code: "uz", label: "Uzbek" }, { code: "vi", label: "Vietnamese" }, { code: "zu", label: "Zulu" },
];


function getAuthBodyText(language: string, withSecurity: boolean): string {
    const translations = AUTH_BODY_TRANSLATIONS[language] || AUTH_BODY_TRANSLATIONS["en"];
    return withSecurity ? translations.withSecurity : translations.default;
}

// ─── UI Helpers ───────────────────────────────────────────────────────────────
const Hint: FC<{ text: React.ReactNode; icon?: any; className?: string; variant?: 'default' | 'danger' | 'warning' | 'info' }> = ({
    text,
    icon: Icon,
    className,
    variant = 'default'
}) => {
    const variants = {
        default: "text-gray-500 dark:text-gray-400",
        danger: "text-red-500 dark:text-red-400",
        warning: "text-amber-500 dark:text-amber-400",
        info: "text-blue-500 dark:text-blue-400",
    };

    const icons = {
        default: Info,
        danger: AlertCircle,
        warning: AlertCircle,
        info: Info,
    };

    const SelectedIcon = Icon || icons[variant];

    return (
        <div className={cn("flex mt-2 items-center gap-1.5 text-[10px] leading-tight", variants[variant], className)}>
            <SelectedIcon className="w-2.5 h-2.5 shrink-0 opacity-80" />
            <span className="flex-1">{text}</span>
        </div>
    );
};

const ErrList: FC<{ errs?: string[] }> = ({ errs }) => {
    if (!errs?.length) return null;
    return (
        <div className="space-y-0.5 mt-1">
            {errs.map((e, i) => (
                <Hint key={i} text={e} variant="danger" />
            ))}
        </div>
    );
};

// Helper to filter errors by field based on error message keywords
const filterErrorsByField = (errs: string[], field: 'text' | 'url' | 'phone' | 'flowId' | 'copyCode' | 'voiceCall' | 'otp'): string[] => {
    if (!errs?.length) return [];

    const fieldKeywords: Record<typeof field, string[]> = {
        text: ['Button label', 'label required', 'max 25 characters', 'variables', 'button text must be unique', 'must be unique'],
        url: ['URL', 'https://', 'wa.me', 'whatsapp.com', 'variable requires example', 'example too long'],
        phone: ['Phone', 'phone', 'international format'],
        flowId: ['Flow ID', '16 digits'],
        copyCode: ['Copy code', '4-15 characters'],
        voiceCall: ['Display text', 'max 20 characters', 'Duration'],
        otp: ['OTP type'],
    };

    const keywords = fieldKeywords[field];
    return errs.filter(err => keywords.some(keyword => err.toLowerCase().includes(keyword.toLowerCase())));
};

const getDuplicateButtonTextKeys = (buttons: ButtonItem[]) => {
    const labelCounts: Record<string, number> = {};
    buttons.forEach((b) => {
        let label = "";
        if (b.type === "voice_call") label = b.voiceCallDisplayText || b.text || "";
        else if (b.type !== "otp" && b.type !== "copy_code") label = b.text || "";
        const key = label.trim().toLowerCase();
        if (!key) return;
        labelCounts[key] = (labelCounts[key] || 0) + 1;
    });
    return new Set(Object.entries(labelCounts).filter(([, count]) => count > 1).map(([label]) => label));
};

const CharCount: FC<{ text?: string; max: number }> = ({ text = "", max }) => {
    const n = text.length, pct = n / max;
    return (
        <span className={cn("text-xs font-mono",
            pct > 0.9 ? "text-red-500 dark:text-red-400" :
                pct > 0.7 ? "text-amber-500 dark:text-amber-400" :
                    "text-gray-500 dark:text-gray-400"
        )}>
            {n}/{max}
        </span>
    );
};

// ─── Button Editor ────────────────────────────────────────────────────────────
interface ButtonEditorProps {
    btn: ButtonItem;
    index: number;
    totalCount: number;
    errors: string[];
    onUpdate: (id: number, patch: Partial<ButtonItem>) => void;
    onBlur?: (field: string) => void;
    onRemove: (id: number) => void;
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent, index: number) => void;
    onDragOver?: (e: React.DragEvent, index: number) => void;
    onDrop?: (e: React.DragEvent, index: number) => void;
    onDragEnd?: (e: React.DragEvent) => void;
    isDragging?: boolean;
    isDragOver?: boolean;
}

const ButtonEditor: FC<ButtonEditorProps> = ({
    btn,
    index,
    errors,
    onUpdate,
    onBlur,
    onRemove,
    draggable = false,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    isDragging = false,
    isDragOver = false,
}) => {
    const typeLabels: Record<ButtonType, string> = {
        quick_reply: "Quick Reply",
        url: "URL",
        phone_number: "Phone",
        copy_code: "Copy Code",
        flow: "Flow",
        voice_call: "Voice Call",
        otp: "OTP",
    };

    const isQr = btn.type === "quick_reply";

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        onDragStart?.(e, index);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver?.(e, index);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        onDrop?.(e, index);
    };

    return (
        <Card
            className={cn(
                "p-4 space-y-4 transition-all duration-200",
                isDragging && "opacity-50 scale-[0.98] ring-2 ring-blue-400 shadow-lg",
                isDragOver && "ring-2 ring-orange-400 border-orange-300 bg-orange-50/30 dark:bg-orange-900/10"
            )}
            draggable={draggable}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={onDragEnd}
        >
            <div className="flex items-start gap-4">
                {/* Reorder Handle */}
                <div className="pt-8 shrink-0">
                    {draggable && (
                        <div className="cursor-grab active:cursor-grabbingtransition-colors">
                            <GripVertical size={16} className="text-gray-400" />
                        </div>
                    )}
                </div>

                {/* Primary Content Grid */}
                <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-4">
                        {/* Column 1: Type / Action Type (Minimized) */}
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
                                {isQr ? "Type" : "Action"}
                            </label>
                            <div className={cn(
                                "h-9 flex items-center px-2 rounded-sm border text-[10px] font-bold",
                                isQr
                                    ? "bg-blue-50/50 text-blue-600 border-blue-100 dark:bg-blue-900/10 dark:border-blue-900/50"
                                    : "bg-orange-50/50 text-orange-600 border-orange-100 dark:bg-orange-900/10 dark:border-orange-900/50"
                            )}>
                                {typeLabels[btn.type.toLowerCase() as ButtonType]}
                                <span className="ml-auto text-[9px] opacity-30">#{index + 1}</span>
                            </div>
                        </div>

                        {/* Column 2: Button Text (Expanded for single-field types) */}
                        <div className={cn(
                            "md:col-span-5",
                            (btn.type === "quick_reply" || btn.type === "otp") && "md:col-span-10"
                        )}>
                            {btn.type !== "otp" && btn.type !== "copy_code" ? (
                                <>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Button text <span className="text-red-500">*</span></label>
                                    <div className="relative group/field">
                                        <input
                                            value={btn.type === 'voice_call' ? (btn.voiceCallDisplayText || "") : btn.text}
                                            onChange={(e) => onUpdate(btn.id, btn.type === 'voice_call' ? { voiceCallDisplayText: e.target.value } : { text: e.target.value })}
                                            onBlur={() => onBlur?.("text")}
                                            placeholder="Enter text..."
                                            className="w-full h-9 bg-white dark:text-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm px-3 text-sm focus:ring-1 focus:ring-blue-500/50 placeholder:text-gray-300 transition-all font-medium"
                                            maxLength={25}
                                        />
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 scale-[0.7] opacity-0 group-hover/field:opacity-100 transition-opacity">
                                            <CharCount text={btn.type === 'voice_call' ? (btn.voiceCallDisplayText || "") : btn.text} max={btn.type === 'voice_call' ? 20 : 25} />
                                        </div>
                                    </div>
                                    <Hint text={btn.type === 'voice_call' ? "Max 20 characters" : "Max 25 characters"} className="mt-0.5 ml-1" />
                                    <ErrList errs={filterErrorsByField(errors, 'text')} />
                                </>
                            ) : (
                                <>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Configuration</label>
                                    <div className="flex items-center h-9 px-3 rounded-sm bg-gray-50/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 italic">
                                        {btn.type === 'otp' && "WhatsApp generated One-Time Password button"}
                                        {btn.type === 'copy_code' && "Fixed Label: Copy offer code"}
                                    </div>
                                    {btn.type === 'otp' && <ErrList errs={filterErrorsByField(errors, 'otp')} />}
                                </>
                            )}
                            {btn.type === 'flow' && <ErrList errs={filterErrorsByField(errors, 'flowId')} />}
                        </div>

                        {/* Column 3: Secondary Action Fields (Contextual, Expanded) */}
                        {btn.type.toLowerCase() === "url" && (
                            <div className="md:col-span-5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Website URL <span className="text-red-500">*</span></label>
                                <input
                                    value={btn.url || ""}
                                    onChange={(e) => onUpdate(btn.id, { url: e.target.value })}
                                    onBlur={() => onBlur?.("url")}
                                    placeholder="https://www.example.com"
                                    className="w-full h-9 bg-white dark:text-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm px-3 text-xs font-mono text-blue-600 dark:text-blue-400 focus:ring-1 focus:ring-blue-500/50"
                                />
                                <Hint text="Must start with https://" className="mt-0.5 ml-1" />
                                <ErrList errs={filterErrorsByField(errors, 'url')} />
                            </div>
                        )}

                        {btn.type.toLowerCase() === "phone_number" && (
                            <div className="md:col-span-5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Phone number <span className="text-red-500">*</span></label>
                                <input
                                    value={btn.phone || ""}
                                    onChange={(e) => onUpdate(btn.id, { phone: e.target.value })}
                                    onBlur={() => onBlur?.("phone")}
                                    placeholder="+1234567890"
                                    className="w-full h-9 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm px-3 text-sm font-mono dark:text-white focus:ring-1 focus:ring-blue-500/50"
                                />
                                <Hint text="International format: +[country][number]" className="mt-0.5 ml-1" />
                                <ErrList errs={filterErrorsByField(errors, 'phone')} />
                            </div>
                        )}

                        {btn.type.toLowerCase() === "copy_code" && (
                            <div className="md:col-span-5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Promotion Code <span className="text-red-500">*</span></label>
                                <div className="relative group/field">
                                    <input
                                        value={btn.copyCodeExample || ""}
                                        onChange={(e) => onUpdate(btn.id, { copyCodeExample: e.target.value })}
                                        onBlur={() => onBlur?.("copyCodeExample")}
                                        placeholder="e.g. SAVE20"
                                        className="w-full h-9 bg-white dark:text-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm px-3 text-sm focus:ring-1 focus:ring-blue-500/50 placeholder:text-gray-300 transition-all font-medium"
                                        maxLength={15}
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 scale-[0.7] opacity-0 group-hover/field:opacity-100 transition-opacity">
                                        <CharCount text={btn.copyCodeExample || ""} max={15} />
                                    </div>
                                </div>
                                <Hint text="Min 4, max 15 characters" className="mt-0.5 ml-1" />
                                <ErrList errs={filterErrorsByField(errors, 'copyCode')} />
                            </div>
                        )}

                        {btn.type.toLowerCase() === "flow" && (
                            <div className="md:col-span-5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Flow ID <span className="text-red-500">*</span></label>
                                <input
                                    value={btn.flowId || ""}
                                    onChange={(e) => onUpdate(btn.id, { flowId: e.target.value })}
                                    onBlur={() => onBlur?.("flowId")}
                                    placeholder="e.g. 1234567890123456"
                                    className="w-full h-9 bg-white dark:text-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm px-3 text-xs font-mono text-blue-600 dark:text-blue-400 focus:ring-1 focus:ring-blue-500/50"
                                    maxLength={16}
                                />
                                <Hint text="Must be a valid 16-digit flow ID" className="mt-0.5 ml-1" />
                                <ErrList errs={filterErrorsByField(errors, 'flowId')} />
                            </div>
                        )}

                        {btn.type.toLowerCase() === "voice_call" && (
                            <div className="md:col-span-5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Call Duration <span className="text-red-500">*</span></label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        value={btn.voiceCallTtl || 10080}
                                        onChange={(e) => onUpdate(btn.id, { voiceCallTtl: parseInt(e.target.value) || 0 })}
                                        onBlur={() => onBlur?.("voiceCallTtl")}
                                        className="w-full h-9 bg-white dark:text-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-sm px-3 text-sm focus:ring-1 focus:ring-blue-500/50 transition-all font-medium"
                                    />
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Min</span>
                                </div>
                                <Hint text="Between 1 and 30 days (1440 - 43200 mins)" className="mt-0.5 ml-1" />
                                <ErrList errs={filterErrorsByField(errors, 'voiceCall')} />
                            </div>
                        )}
                    </div>

                </div>

                {/* Delete Button - Aligned with the input row */}
                <div className="pt-[22px] shrink-0">
                    <button
                        type="button"
                        onClick={() => onRemove(btn.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded transition-all"
                        title="Remove button"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>


        </Card>
    );
};

// ─── Sync Logic ──────────────────────────────────────────────────────────────
const SyncValues: React.FC<{ setWhatsAppValues: (v: any) => void }> = ({ setWhatsAppValues }) => {
    const { values } = useFormikContext<FormValues>();
    const prev = useRef<string>("");
    const setWhatsAppValuesRef = useRef(setWhatsAppValues);

    // Keep ref current without triggering re-renders
    useEffect(() => {
        setWhatsAppValuesRef.current = setWhatsAppValues;
    }, []);

    useEffect(() => {
        const serialized = JSON.stringify(values);
        if (prev.current !== serialized) {
            prev.current = serialized;
            setWhatsAppValuesRef.current(values);
        }
    }, [values]); // only values — setWhatsAppValues is accessed via stable ref

    return null;
};

// ─── Inner Form ───────────────────────────────────────────────────────────────
const TemplateForm: FC<{
    chatBotId: string;
    canDraft: boolean;
    isTemplateNameDisabled?: boolean;
    templateId?: string;
    isLibraryTemplate?: boolean;
    onUserEdited: () => void;       // ← new
    onSubmitSuccess: () => void;    // ← new
    isEditMode: boolean;            // ← new (replaces location.pathname check)
    templateData?: any;
}> = ({ chatBotId, canDraft, isTemplateNameDisabled, templateId, isLibraryTemplate, onUserEdited, onSubmitSuccess, isEditMode, templateData }) => {
    const { user } = useAuth();
    const isSuperAdmin = user?.access?.accountType === "superAdmin";
    const { data: libraryFilters } = useTemplateLibraryFiltersQuery({ enabled: isSuperAdmin });
    const headerTypeOptions = isSuperAdmin
        ? [
            { type: "TEXT", icon: Type, title: "Text", sub: "Alpha-numeric & vars" },
          ]
        : [
            { type: "TEXT", icon: Type, title: "Text", sub: "Alpha-numeric & vars" },
            { type: "IMAGE", icon: Image, title: "Image", sub: "JPG, PNG, max 5MB" },
            { type: "VIDEO", icon: Video, title: "Video", sub: "MP4, max 16MB" },
            { type: "DOCUMENT", icon: FileText, title: "Document", sub: "PDF, max 100MB" },
          ];
    // ── Validation visibility ────────────────────────────────────────────
    const [isSubmitted, setIsSubmitted] = useState(false);

    // ── Edit tracking ────────────────────────────────────────────────────
    const hasLoadedRef = useRef(false);

    const { values, errors, touched, setFieldValue, setFieldTouched, dirty, submitCount } = useFormikContext<FormValues>();

    useEffect(() => {
        if (isLibraryTemplate) {
            setFieldTouched("templateName", true, false);
        }
    }, [isLibraryTemplate, setFieldTouched]);

    const navigate = useNavigate();

    useEffect(() => {
        if (isEditMode && templateData) {
            // Wait for Formik's enableReinitialize to settle before tracking edits
            const t = setTimeout(() => {
                hasLoadedRef.current = true;
            }, 300);
            return () => clearTimeout(t);
        } else {
            // Create mode — track immediately
            hasLoadedRef.current = true;
        }
    }, [isEditMode, templateData]);

    // Signal parent whenever dirty becomes true after load
    useEffect(() => {
        if (hasLoadedRef.current && dirty) {
            onUserEdited();
        }
    }, [dirty, onUserEdited]);

    // Get context from layout
    const { setHeaderActions, setCustomHeader, setWhatsAppValues, isTrialBannerShown } = useOutletContext<any>() || {};

    const [bodyCaretPos, setBodyCaretPos] = useState<number | null>(null);
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadMediaMutation = useUploadTemplateMedia();
    const createTemplateMutation = useCreateTemplate();
    const updateTemplateMutation = useUpdateTemplate();
    const [isCreating, setIsCreating] = useState(false);
    const [isDrafting, setIsDrafting] = useState(false);
    const [showMediaLibrary, setShowMediaLibrary] = useState(false);
    const [libraryMeta, setLibraryMeta] = useState({
        templateLibraryIndustryId: "",
        templateLibrarySubcategoryId: "",
        libraryPreviewContent: "",
    });
    const [isAddIndustryModalOpen, setIsAddIndustryModalOpen] = useState(false);
    const [isAddSubcategoryModalOpen, setIsAddSubcategoryModalOpen] = useState(false);
    const prevLibraryCategoryRef = useRef<string | null>(null);
    const headerMediaCacheRef = useRef<Record<"IMAGE" | "VIDEO" | "DOCUMENT", Pick<HeaderState, "mediaExample" | "mediaPreview" | "fileName" | "fileSize" | "pageCount">>>({
        IMAGE: { mediaExample: "", mediaPreview: "", fileName: "", fileSize: "", pageCount: undefined },
        VIDEO: { mediaExample: "", mediaPreview: "", fileName: "", fileSize: "", pageCount: undefined },
        DOCUMENT: { mediaExample: "", mediaPreview: "", fileName: "", fileSize: "", pageCount: undefined },
    });

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node))
                setShowEmojiPicker(false);
        };
        if (showEmojiPicker) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showEmojiPicker]);

    useEffect(() => {
        if (values.header.type === "IMAGE" || values.header.type === "VIDEO" || values.header.type === "DOCUMENT") {
            if (values.header.mediaExample || values.header.mediaPreview || values.header.fileName) {
                const cached = headerMediaCacheRef.current[values.header.type];
                if (!cached.mediaExample && !cached.mediaPreview && !cached.fileName) {
                    headerMediaCacheRef.current[values.header.type] = {
                        mediaExample: values.header.mediaExample,
                        mediaPreview: values.header.mediaPreview,
                        fileName: values.header.fileName || "",
                        fileSize: values.header.fileSize || "",
                        pageCount: values.header.pageCount,
                    };
                }
            }
        }
    }, [values.header.type, values.header.mediaExample, values.header.mediaPreview, values.header.fileName, values.header.fileSize, values.header.pageCount]);

    // Count all errors (flatten all yup errors for display)
    const totalErrors = Object.keys(errors).length;
    const isValid = totalErrors === 0 && !!values.body.text;
    const isAuth = values.category === "AUTHENTICATION";
    const hasOTP = values.buttons.some((b) => b.type === "otp");
    const lockedType: LockedType = hasOTP ? "otp" : null;

    const headerHasVars = (values.variableType === "positional" && extractVars(values.header.text).length > 0) ||
        (values.variableType === "named" && extractNamedVars(values.header.text).length > 0);
    const headerExampleRequired = values.header.type === "TEXT" && headerHasVars && !values.header.example?.trim();
    const libraryMetaIsValid = !isSuperAdmin || (!!libraryMeta.templateLibraryIndustryId && !!libraryMeta.templateLibrarySubcategoryId);
    const isSubmitDisabled = isCreating || isDrafting || totalErrors > 0 || (!dirty && !isLibraryTemplate) || headerExampleRequired || !libraryMetaIsValid;

    // ── Helpers (same logic as original) ─────────────────────────────────────
    const handleSetCategory = (cat: Category) => {
        setFieldValue("category", cat);
        setFieldValue("header", { enabled: false, type: "TEXT", text: "", mediaExample: "", mediaPreview: "", example: "" });
        setFieldValue("footer", { enabled: false, text: "", addSecurityRecommendation: false, codeExpirationMinutes: undefined });
        setFieldValue("buttons", []);
        setFieldValue("body", cat === "AUTHENTICATION"
            ? { text: getAuthBodyText(values.language, false), examples: [""] }
            : { text: "", examples: [] });
    };

    const handleSetVariableType = (vt: "positional" | "named") => {
        setFieldValue("variableType", vt);
    };

    const addButton = (type: ButtonType) => {
        const buttons = values.buttons;
        if (buttons.length >= 10) return;
        const hasOTPBtn = buttons.some((b) => b.type === "otp");
        if (type === "otp") { if (buttons.length > 0 || values.category !== "AUTHENTICATION") return; }
        if (hasOTPBtn) return;
        if (values.category === "AUTHENTICATION" && type !== "otp") return;
        if (type === "quick_reply" && buttons.filter((b) => b.type === "quick_reply").length >= 10) return;
        if (type === "url" && buttons.filter((b) => b.type === "url").length >= 2) return;
        if (type === "phone_number" && buttons.some((b) => b.type === "phone_number" || b.type === "voice_call")) return;
        if (type === "copy_code" && buttons.filter((b) => b.type === "copy_code").length >= 1) return;
        if (type === "flow" && buttons.filter((b) => b.type === "flow").length >= 1) return;
        if (type === "voice_call" && buttons.some((b) => b.type === "voice_call" || b.type === "phone_number")) return;
        const newBtn: ButtonItem = {
            id: buttons.length ? Math.max(...buttons.map((b) => b.id)) + 1 : Date.now(),
            type, text: "",
        };
        if (type === "otp") newBtn.otpType = "copy_code";
        if (type === "phone_number") newBtn.phone = "";
        if (type === "voice_call") { newBtn.voiceCallDisplayText = "Call on WhatsApp"; newBtn.voiceCallTtl = 10080; }
        setFieldValue("buttons", reorderButtonsForGrouping([...buttons, newBtn]));
    };

    const updateButton = (id: number, patch: Partial<ButtonItem>) =>
        setFieldValue("buttons", reorderButtonsForGrouping(values.buttons.map((b) => b.id === id ? { ...b, ...patch } : b)));

    const removeButton = (id: number) =>
        setFieldValue("buttons", reorderButtonsForGrouping(values.buttons.filter((b) => b.id !== id)));

    // ─── Drag and Drop State ─────────────────────────────────────────────────────
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [groupOrder, setGroupOrder] = useState<('quick_reply' | 'cta')[]>(['quick_reply', 'cta']);
    const [draggedGroup, setDraggedGroup] = useState<'quick_reply' | 'cta' | null>(null);
    const [dragOverGroup, setDragOverGroup] = useState<'quick_reply' | 'cta' | null>(null);

    const handleGroupDragStart = (e: React.DragEvent, group: 'quick_reply' | 'cta') => {
        setDraggedGroup(group);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleGroupDragOver = (e: React.DragEvent, group: 'quick_reply' | 'cta') => {
        e.preventDefault();
        if (draggedGroup === null || draggedGroup === group) return;
        setDragOverGroup(group);
    };

    const handleGroupDrop = (e: React.DragEvent, group: 'quick_reply' | 'cta') => {
        e.preventDefault();
        if (draggedGroup && draggedGroup !== group) {
            const newOrder = [...groupOrder];
            const fromIdx = newOrder.indexOf(draggedGroup);
            const toIdx = newOrder.indexOf(group);
            [newOrder[fromIdx], newOrder[toIdx]] = [newOrder[toIdx], newOrder[fromIdx]];
            setGroupOrder(newOrder);
            setFieldValue("buttons", reorderButtonsForGrouping(values.buttons, newOrder));
        }
        setDraggedGroup(null);
        setDragOverGroup(null);
    };
    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        // Restriction: Only allow dragging within the same group (Quick Reply vs Others)
        const draggedBtn = values.buttons[draggedIndex];
        const targetBtn = values.buttons[index];
        const draggedIsQr = draggedBtn.type === "quick_reply";
        const targetIsQr = targetBtn.type === "quick_reply";

        if (draggedIsQr !== targetIsQr) return;

        setDragOverIndex(index);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const buttons = [...values.buttons];
        const draggedBtn = buttons[draggedIndex];
        const targetBtn = buttons[dropIndex];
        const draggedIsQr = draggedBtn.type === "quick_reply";
        const targetIsQr = targetBtn.type === "quick_reply";

        if (draggedIsQr === targetIsQr) {
            // Same type - simple reorder within group
            const [removed] = buttons.splice(draggedIndex, 1);
            buttons.splice(dropIndex, 0, removed);
            setFieldValue("buttons", buttons);
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const nextBodyVar = () => {
        if (values.variableType === "positional") {
            const v = extractVars(values.body.text);
            return `{{${v.length ? Math.max(...v) + 1 : 1}}}`;
        }
        return `{{variable_${extractNamedVars(values.body.text).length + 1}}}`;
    };

    const insertBodyVar = () => {
        const pos = bodyCaretPos ?? values.body.text.length;
        setFieldValue("body.text", insertAtCursor(values.body.text, nextBodyVar(), pos));
    };
    const insertFormatting = (format: string) => {
        const textarea = bodyTextareaRef.current;
        const start = textarea?.selectionStart ?? bodyCaretPos ?? values.body.text.length;
        const end = textarea?.selectionEnd ?? start;
        const selectedText = values.body.text.substring(start, end);

        if (selectedText) {
            const marker = format;
            const newText = values.body.text.substring(0, start) + marker + selectedText + marker + values.body.text.substring(end);
            setFieldValue("body.text", newText);
            const newPos = start + marker.length + selectedText.length + marker.length;
            setBodyCaretPos(newPos);
            setTimeout(() => { textarea?.setSelectionRange(newPos, newPos); textarea?.focus(); }, 0);
        } else {
            const pos = bodyCaretPos ?? values.body.text.length;
            const marker = format;
            setFieldValue("body.text", insertAtCursor(values.body.text, `${marker}${marker}`, pos));
            const newPos = pos + marker.length;
            setBodyCaretPos(newPos);
            setTimeout(() => { textarea?.setSelectionRange(newPos, newPos); textarea?.focus(); }, 0);
        }
    };

    const reorderButtonsForGrouping = (buttons: ButtonItem[], order = groupOrder) => {
        const qr = buttons.filter((b) => b.type === "quick_reply");
        const nonQr = buttons.filter((b) => b.type !== "quick_reply");
        if (qr.length === 0 || nonQr.length === 0) return buttons;

        return order[0] === 'quick_reply' ? [...qr, ...nonQr] : [...nonQr, ...qr];
    };

    const getHeaderStateForType = (type: HeaderType): HeaderState => {
        if (type === "TEXT") {
            return {
                type,
                text: values.header.text,
                mediaExample: "",
                mediaPreview: "",
                example: values.header.example || "",
                fileName: "",
                fileSize: "",
                pageCount: undefined,
            };
        }
        if (type === "LOCATION") {
            return {
                type,
                text: "",
                mediaExample: "",
                mediaPreview: "",
                example: "",
                fileName: "",
                fileSize: "",
                pageCount: undefined,
            };
        }
        const cached = headerMediaCacheRef.current[type];
        return {
            type,
            text: "",
            mediaExample: cached?.mediaExample || "",
            mediaPreview: cached?.mediaPreview || "",
            example: "",
            fileName: cached?.fileName || "",
            fileSize: cached?.fileSize || "",
            pageCount: cached?.pageCount,
        };
    };

    const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const pos = e.target.selectionStart;
        if (value.length > values.body.text.length && pos >= 2 && value.slice(pos - 2, pos) === "{{") {
            const newText = value.slice(0, pos - 2) + "{{}}" + value.slice(pos);
            setFieldValue("body.text", newText);
            setBodyCaretPos(pos);
            setTimeout(() => { bodyTextareaRef.current?.setSelectionRange(pos, pos); bodyTextareaRef.current?.focus(); }, 0);
            return;
        }
        setFieldValue("body.text", value);
        setBodyCaretPos(pos);
    };

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        const pos = bodyCaretPos ?? values.body.text.length;
        setFieldValue("body.text", insertAtCursor(values.body.text, emojiData.emoji, pos));
        setBodyCaretPos(pos + emojiData.emoji.length);
    };

    const handleImageUpload = useCallback(async (file: File) => {
        if (!chatBotId) { toast.error("Chat bot ID is required"); return; }

        const headerType = values.header.type;
        const config = MEDIA_CONFIG[headerType];

        if (!config || headerType === "TEXT" || headerType === "LOCATION") {
            toast.error("Invalid header type for media upload");
            return;
        }

        const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
        if (!config.extensions.includes(fileExt)) {
            toast.error(`Invalid file format. Supported formats: ${config.extensions.join(", ").toUpperCase()}`);
            return;
        }

        if (file.size > config.maxSize) {
            const maxMB = config.maxSize / (1024 * 1024);
            toast.error(`${headerType} must be less than ${maxMB}MB`);
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setUploadingMedia(true);
        try {
            const result = await uploadMediaMutation.mutateAsync({
                chatBotId,
                file,
                type: headerType.toLowerCase() as "image" | "video" | "document"
            });
            if (result?.data?.handle) {
                const finalMediaUrl = result.data.mediaUrl || previewUrl;
                if (headerType === "IMAGE" || headerType === "VIDEO" || headerType === "DOCUMENT") {
                    headerMediaCacheRef.current[headerType] = {
                        mediaExample: result.data.handle,
                        mediaPreview: finalMediaUrl,
                        fileName: file.name,
                        fileSize: formatFileSize(file.size),
                        pageCount: values.header.pageCount,
                    };
                    (["IMAGE", "VIDEO", "DOCUMENT"] as const).forEach((t) => {
                        if (t !== headerType) {
                            headerMediaCacheRef.current[t] = {
                                mediaExample: "",
                                mediaPreview: "",
                                fileName: "",
                                fileSize: "",
                                pageCount: undefined,
                            };
                        }
                    });
                }
                setFieldValue("header", {
                    ...values.header,
                    type: headerType,
                    enabled: true,
                    mediaExample: result.data.handle,
                    mediaPreview: finalMediaUrl,
                    fileName: file.name,
                    fileSize: formatFileSize(file.size)
                });
            }
        } catch (err) {
            console.error("Upload error:", err);
            URL.revokeObjectURL(previewUrl);
        } finally {
            setUploadingMedia(false);
        }
    }, [chatBotId, uploadMediaMutation, values.header]);

    const handleMediaLibrarySelect = useCallback(async (
        mediaUrl: string
    ) => {
        if (!chatBotId) { toast.error("Chat bot ID is required"); return; }
        const headerType = values.header.type;
        if (headerType === "TEXT" || headerType === "LOCATION") return;

        setUploadingMedia(true);
        try {
            const result = await uploadMediaMutation.mutateAsync({
                chatBotId,
                file: null,
                type: headerType.toLowerCase() as "image" | "video" | "document",
                mediaUrl
            });
            if (result?.data?.handle) {
                const fileName = mediaUrl.split("/").pop() || "Media from Library";
                headerMediaCacheRef.current[headerType] = {
                    mediaExample: result.data.handle,
                    mediaPreview: mediaUrl,
                    fileName: fileName,
                    fileSize: "External Library",
                    pageCount: values.header.pageCount,
                };
                setFieldValue("header", {
                    ...values.header,
                    type: headerType,
                    enabled: true,
                    mediaExample: result.data.handle,
                    mediaPreview: mediaUrl,
                    fileName: fileName,
                    fileSize: "External Library"
                });
            }
        } catch (err) {
            console.error("Library select error:", err);
        } finally {
            setUploadingMedia(false);
            setShowMediaLibrary(false);
        }
    }, [chatBotId, uploadMediaMutation, values.header, setFieldValue]);

    const handleRemoveMedia = useCallback(() => {
        const type = values.header.type;
        if (type === "TEXT" || type === "LOCATION") return;

        // Clear current state
        setFieldValue("header", {
            ...values.header,
            mediaExample: "",
            mediaPreview: "",
            fileName: "",
            fileSize: "",
            pageCount: undefined
        });

        // Clear cache as well
        if (type === "IMAGE" || type === "VIDEO" || type === "DOCUMENT") {
            headerMediaCacheRef.current[type] = {
                mediaExample: "",
                mediaPreview: "",
                fileName: "",
                fileSize: "",
                pageCount: undefined
            };
        }
    }, [values.header, setFieldValue]);


    // Update body text when language changes for AUTHENTICATION
    useEffect(() => {
        if (values.category === "AUTHENTICATION" && hasLoadedRef.current) {
            setFieldValue("body", {
                text: getAuthBodyText(values.language, values.footer.addSecurityRecommendation || false),
                examples: values.body.examples
            });
        }
    }, [values.language, values.category, values.footer.addSecurityRecommendation]);

    useEffect(() => {
        if (!isSuperAdmin) {
            prevLibraryCategoryRef.current = null;
            setLibraryMeta({
                templateLibraryIndustryId: "",
                templateLibrarySubcategoryId: "",
                libraryPreviewContent: "",
            });
            return;
        }

        const template = templateData?.data;
        if (template?.templateLibraryIndustryId || template?.templateLibrarySubcategoryId || template?.libraryPreviewContent) {
            setLibraryMeta({
                templateLibraryIndustryId: template.templateLibraryIndustryId || "",
                templateLibrarySubcategoryId: template.templateLibrarySubcategoryId || "",
                libraryPreviewContent: template.libraryPreviewContent || "",
            });
            prevLibraryCategoryRef.current = template.category ? template.category.toUpperCase() : null;
        }
    }, [isSuperAdmin, templateData]);

    useEffect(() => {
        if (!isSuperAdmin) {
            return;
        }

        if (!prevLibraryCategoryRef.current) {
            prevLibraryCategoryRef.current = values.category;
            return;
        }

        if (prevLibraryCategoryRef.current === values.category) {
            return;
        }

        prevLibraryCategoryRef.current = values.category;
        setLibraryMeta({
            templateLibraryIndustryId: "",
            templateLibrarySubcategoryId: "",
            libraryPreviewContent: "",
        });
    }, [isSuperAdmin, values.category]);

    // ── Update submit handlers to signal success ──────────────────────────
    const handleDirectSubmit = useCallback(async () => {
        setIsSubmitted(true);
        if (!chatBotId) { toast.error("Chat bot ID is required"); return; }
        if (isSuperAdmin && (!libraryMeta.templateLibraryIndustryId || !libraryMeta.templateLibrarySubcategoryId)) {
            toast.error("Industry and Subcategory are required for library templates.");
            return;
        }
        if (!isValid || totalErrors > 0 || headerExampleRequired) { toast.error("Please fix all errors before submitting"); return; }
        try {
            setIsCreating(true);
            if (isEditMode) {
                await updateTemplateMutation.mutateAsync({
                    templateId: templateId!,
                    data: generateJSON(values, isSuperAdmin ? libraryMeta : undefined),
                    status: 'submit'
                });
            } else {
                await createTemplateMutation.mutateAsync({
                    chatBotId,
                    data: generateJSON(values, isSuperAdmin ? libraryMeta : undefined),
                    status: 'submit'
                });
            }

            onSubmitSuccess(); // ← signal before navigate
            navigate(`/chatbot-builder/${chatBotId}/whatsapp/templates`);
        } catch (err) { console.error("Submit error:", err); } finally {
            setIsCreating(false);
        }
    }, [chatBotId, isValid, totalErrors, isEditMode, updateTemplateMutation, templateId, values, createTemplateMutation, navigate, onSubmitSuccess, headerExampleRequired, isSuperAdmin, libraryMeta]);

    const handleDraft = useCallback(async () => {
        setIsSubmitted(true);
        if (!chatBotId) { toast.error("Chat bot ID is required"); return; }
        if (isEditMode && !templateId) { toast.error("Template ID is required"); return; }
        if (isSuperAdmin && (!libraryMeta.templateLibraryIndustryId || !libraryMeta.templateLibrarySubcategoryId)) {
            toast.error("Industry and Subcategory are required for library templates.");
            return;
        }
        if (!isValid || totalErrors > 0 || headerExampleRequired) { toast.error("Please fix all errors before saving"); return; }
        try {
            setIsDrafting(true);
            if (isEditMode) {
                await updateTemplateMutation.mutateAsync({
                    templateId: templateId!,
                    data: generateJSON(values, isSuperAdmin ? libraryMeta : undefined),
                    status: 'draft'
                });
            } else {
                await createTemplateMutation.mutateAsync({
                    chatBotId,
                    data: generateJSON(values, isSuperAdmin ? libraryMeta : undefined),
                    status: 'draft'
                });
            }
            onSubmitSuccess();
            navigate(`/chatbot-builder/${chatBotId}/whatsapp/templates`);
        } catch (err) { console.error("Submit error:", err); } finally {
            setIsDrafting(false);
        }
    }, [chatBotId, isValid, totalErrors, isEditMode, templateId, updateTemplateMutation, createTemplateMutation, values, navigate, onSubmitSuccess, headerExampleRequired, isSuperAdmin, libraryMeta]);

    // Update Layout Header
    useEffect(() => {
        if (!setHeaderActions || !setCustomHeader) return;

        setCustomHeader({
            heading: isEditMode ? "Edit Template" : "WhatsApp Template Builder",
            description: "Meta Cloud API Compliant",
            showBackButton: true,
            onBack: () => navigate(-1),
        });

        return () => {
            setHeaderActions?.(null);
            setCustomHeader?.(null);
        };
    }, [isCreating, isDrafting, totalErrors, isEditMode, canDraft, updateTemplateMutation.isPending, headerExampleRequired]);

    // ── Field error helpers ────────────────────────────────────────────────
    const errsFor = (path: string, forceTouched = false): string[] => {
        const checkTouched = () => {
            const keys = path.split(".");
            let cur: any = touched;
            for (const k of keys) {
                if (cur == null || typeof cur !== "object") return false;
                cur = cur[k];
            }
            if (Array.isArray(cur)) return cur.some((item) => !!item);
            return !!cur;
        };
        if (!forceTouched && !isSubmitted && submitCount === 0 && !checkTouched()) return [];
        const keys = path.split(".");
        let cur: any = errors;
        for (const k of keys) {
            if (cur == null || typeof cur !== "object") return [];
            cur = cur[k];
        }
        if (cur == null) return [];
        return splitErrs(typeof cur === "string" ? cur : undefined);
    };

    const firstErrFor = (path: string, forceTouched = false): string | undefined => {
        const list = errsFor(path, forceTouched);
        return list.length > 0 ? list[0] : undefined;
    };

    // Per-button errors from Yup nested error object
    const errsForBtn = (index: number): string[] => {
        const btnTouched = touched.buttons?.[index];
        const isFormSubmitted = isSubmitted || submitCount > 0;
        const btn = values.buttons?.[index];
        const duplicateKeys = getDuplicateButtonTextKeys(values.buttons || []);

        const btnText = btn ? (btn.type === 'voice_call' ? (btn.voiceCallDisplayText || btn.text || "") : btn.text || "") : "";
        const duplicateErrors = btnText.trim() && duplicateKeys.has(btnText.trim().toLowerCase())
            ? ["Button text must be unique"]
            : [];

        // If not submitted AND (not touched at all OR (it's an object/array and nothing in it is touched))
        // only show duplicate errors immediately; other field errors require touch/submit.
        const hasTouchedFields = btnTouched && (typeof btnTouched !== 'object' || Object.values(btnTouched).some(val => !!val));
        if (!isFormSubmitted && !hasTouchedFields && !duplicateErrors.length) {
            return [];
        }

        const btns = (errors as any)?.buttons;
        const addDuplicateError = (fieldErrors: string[]) => {
            return [...new Set([...fieldErrors, ...duplicateErrors])];
        };

        if (!btns) return duplicateErrors;
        if (typeof btns === "string") {
            // Array-level errors are shown globally; for per-button error display,
            // only include the duplicate label error when this button is part of the duplicate set.
            return duplicateErrors;
        }
        // After nested conversion, buttons is an array where each item is
        // either a string (from object-level .test) or an object with field keys
        if (!Array.isArray(btns)) return duplicateErrors;
        const item = btns[index];
        if (item == null) return duplicateErrors;
        if (typeof item === "string") {
            const fieldErrors = splitErrs(item);
            return addDuplicateError(fieldErrors);
        }
        if (typeof item === "object") {
            // Collect all string leaf values (each field's error message)
            const fieldErrors = Object.values(item).flatMap((v) =>
                typeof v === "string" ? splitErrs(v) : []
            );
            return addDuplicateError(fieldErrors);
        }
        return duplicateErrors;
    };

    // Array-level button errors (max counts, OTP isolation, etc.)
    const arrayBtnErrs = (): string[] => {
        const btns = (errors as any)?.buttons;
        if (typeof btns === "string") {
            // Show array-level button errors immediately when they exist
            return splitErrs(btns);
        }
        return [];
    };

    const currentLibraryCategory = libraryFilters?.categories.find(
        (category) => category.key === values.category.toLowerCase()
    );
    const currentLibraryIndustries = currentLibraryCategory?.industries ?? [];
    const currentLibraryIndustry = currentLibraryIndustries.find(
        (industry) => industry.id === libraryMeta.templateLibraryIndustryId
    );
    const currentLibrarySubcategories = currentLibraryIndustry?.subcategories ?? [];

    const handleLibraryIndustryChange = (industryId: string) => {
        setLibraryMeta((prev) => ({
            ...prev,
            templateLibraryIndustryId: industryId,
            templateLibrarySubcategoryId: "",
        }));
    };

    const handleLibrarySubcategoryChange = (subcategoryId: string) => {
        setLibraryMeta((prev) => ({
            ...prev,
            templateLibrarySubcategoryId: subcategoryId,
        }));
    };

    const libraryMetaErrors = useMemo(() => {
        if (!isSuperAdmin) {
            return { industry: '', subcategory: '' };
        }

        return {
            industry: isSubmitted && !libraryMeta.templateLibraryIndustryId ? 'Industry is required.' : '',
            subcategory: isSubmitted && !libraryMeta.templateLibrarySubcategoryId ? 'Subcategory is required.' : '',
        };
    }, [isSuperAdmin, isSubmitted, libraryMeta.templateLibraryIndustryId, libraryMeta.templateLibrarySubcategoryId]);

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className={cn(
            "flex flex-col bg-gray-50/50 dark:bg-gray-900 -m-4",
            isTrialBannerShown ? "h-[calc(100vh-180px)] lg:h-[calc(100vh-110px)]" : "h-[calc(100vh-177px)] lg:h-[calc(100vh-116px)]"
        )}>
            {/* <div className="bg-linear-to-r from-pink-50 via-purple-50 to-indigo-50 dark:from-pink-900/10 dark:via-purple-900/10 dark:to-indigo-900/10 px-6 py-2 border-b border-purple-200 dark:border-purple-800/20">
                <div className="flex items-center justify-center gap-4 max-w-4xl mx-auto flex-wrap">
                    <p className="text-sm text-center text-gray-700 dark:text-gray-300 flex gap-2">
                        <Lightbulb className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0" />

                        <span className="font-semibold text-purple-900 dark:text-purple-300">Looking for inspiration?</span> Check out our collection of ready-to-use template samples
                    </p>
                    <button onClick={() => navigate(`/chatbot-builder/${chatBotId}/whatsapp/templates/samples`)} className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-full hover:bg-purple-700 transition-colors whitespace-nowrap shadow-md">
                        Get Started
                    </button>
                </div>
            </div> */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                <div className="max-w-[1800px] mx-auto">
                    <div className="flex flex-col gap-6">
                        {/* ── Form ── */}
                        <div className={cn("space-y-6", !setWhatsAppValues && "")}>
                            {setWhatsAppValues && <SyncValues setWhatsAppValues={setWhatsAppValues} />}

                            {/* Basic Info */}
                            <Card>
                                <CardHeader className="flex flex-row items-center gap-4 py-4">
                                    <div className="w-10 h-10 rounded-sm bg-blue-50 dark:bg-blue-600/30 flex items-center justify-center shrink-0">
                                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Basic Information</h2>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Specify the template name, category, and language.</p>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <label htmlFor="templateName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Template Name <span className="text-red-500">*</span>
                                            </label>
                                            <Hint text="Lowercase letters, numbers, and underscores only (max 512 chars)" className="mt-0" />
                                        </div>
                                        <Input
                                            name="templateName"
                                            id="templateName"
                                            type="text"
                                            value={values.templateName}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const noEmojis = e.target.value.replace(emojiRegex(), "");
                                                const processed = noEmojis.replace(/\s+/g, "_").toLowerCase();
                                                setFieldValue("templateName", processed);
                                            }}
                                            onBlur={() => setFieldTouched("templateName", true)}
                                            placeholder="e.g. order_shipped_update"
                                            disabled={isTemplateNameDisabled}
                                            error={!!firstErrFor("templateName")}
                                        />
                                        {isTemplateNameDisabled && (
                                            <Hint text="Template name cannot be edited after approval" variant="warning" className="-mt-1" />
                                        )}
                                        {firstErrFor("templateName") && (
                                            <Hint text={firstErrFor("templateName")} variant="danger" />
                                        )}
                                    </div>
                                    {/* Show remaining errors after first */}
                                    <ErrList errs={errsFor("templateName").slice(1)} />
                                    {values.templateName && (
                                        <div className="flex items-center justify-end -mt-2.5">
                                            <CharCount text={values.templateName} max={512} />
                                        </div>
                                    )}

                                    <div className="grid grid-cols-3 gap-3">
                                        <div >
                                            <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">Category <span className="text-red-500">*</span></Label>
                                            <CustomSelect
                                                value={values.category}
                                                disabled={isTemplateNameDisabled}
                                                onChange={(val) => handleSetCategory(val as Category)}
                                                options={[
                                                    { value: "UTILITY", label: "UTILITY" },
                                                    { value: "MARKETING", label: "MARKETING" },
                                                    { value: "AUTHENTICATION", label: "AUTHENTICATION" },
                                                ]}
                                            />
                                            {isTemplateNameDisabled && (
                                                <Hint text="Template Category cannot be edited after approval" variant="warning" className="mt-0.5" />
                                            )}
                                        </div>
                                        <div>
                                            <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">Language <span className="text-red-500">*</span></Label>
                                            <CustomSelect
                                                value={values.language}
                                                onChange={(val) => setFieldValue("language", val)}
                                                options={LANGUAGES.map((l) => ({ value: l.code, label: l.label }))}
                                                searchable
                                                disabled={isTemplateNameDisabled}
                                            />
                                            {isTemplateNameDisabled && (
                                                <Hint text="Template Language cannot be edited after approval" variant="warning" className="-mt-1" />
                                            )}
                                        </div>
                                        {!isAuth && (
                                            <div>
                                                <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">Variable Type</Label>
                                                <CustomSelect
                                                    value={values.variableType}
                                                    onChange={(val) => handleSetVariableType(val as "positional" | "named")}
                                                    options={[
                                                        { value: "positional", label: `Positional ({1})` },
                                                        { value: "named", label: `Named ({name})` },
                                                    ]}
                                                />
                                                <Hint text={values.variableType === "positional" ? "Sequential: {{1}}, {{2}}, {{3}}" : "Named: {{customer}}, {{order_id}}"} />
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {isSuperAdmin && (
                                <Card>
                                    <CardHeader className="flex flex-row items-center gap-4 py-4">
                                        <div className="w-10 h-10 rounded-sm bg-emerald-50 dark:bg-emerald-600/30 flex items-center justify-center shrink-0">
                                            <LayoutTemplate className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <div className="flex-1">
                                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Library Mapping</h2>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Link this predefined template to an industry and subcategory.
                                            </p>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <div className="space-y-1.5">
                                                <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
                                                    Industry <span className="text-red-500">*</span>
                                                </Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1">
                                                        <CustomSelect
                                                            value={libraryMeta.templateLibraryIndustryId}
                                                            onChange={handleLibraryIndustryChange}
                                                            options={currentLibraryCategory?.industries.map((industry) => ({
                                                                value: industry.id,
                                                                label: industry.label,
                                                            })) ?? []}
                                                            searchable
                                                            placeholder="Select industry"
                                                        />
                                                        {libraryMetaErrors.industry && (
                                                            <p className="text-xs text-red-600 mt-1">{libraryMetaErrors.industry}</p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        className="w-10 h-10 shrink-0"
                                                        onClick={() => setIsAddIndustryModalOpen(true)}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
                                                    Subcategory <span className="text-red-500">*</span>
                                                </Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1">
                                                        <CustomSelect
                                                            value={libraryMeta.templateLibrarySubcategoryId}
                                                            onChange={handleLibrarySubcategoryChange}
                                                            options={currentLibrarySubcategories.map((subcategory) => ({
                                                                value: subcategory.id,
                                                                label: subcategory.label,
                                                            }))}
                                                            searchable
                                                            placeholder="Select subcategory"
                                                            disabled={!libraryMeta.templateLibraryIndustryId}
                                                        />
                                                        {libraryMetaErrors.subcategory && (
                                                            <p className="text-xs text-red-600 mt-1">{libraryMetaErrors.subcategory}</p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="icon"
                                                        className="w-10 h-10 shrink-0"
                                                        onClick={() => setIsAddSubcategoryModalOpen(true)}
                                                        disabled={!libraryMeta.templateLibraryIndustryId}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        <Hint
                                            text="This metadata is saved only for super-admin predefined templates."
                                            variant="info"
                                        />
                                    </CardContent>
                                </Card>
                            )}

                            {/* Auth notice */}
                            {isAuth && (
                                <Card className="p-4 bg-amber-50 dark:bg-amber-950/25 border-amber-200 dark:border-amber-900/30">
                                    <Label className="text-amber-800 dark:text-amber-300 font-bold mb-3 block">AUTHENTICATION Template Requirements</Label>
                                    <div className="space-y-2">
                                        <Hint variant="warning" text="Header disabled by Meta" />
                                        <Hint variant="warning" text="Footer disabled by Meta" />
                                        <Hint variant="warning" text={<><strong>Must include OTP button</strong> (copy_code)</>} />
                                        <Hint variant="warning" text="Body: exactly 1 variable for the OTP code" />
                                        <Hint variant="warning" text="No Quick Reply, URL, or Phone buttons allowed" />
                                    </div>
                                    <Hint variant="info" className="mt-4 p-2 bg-blue-50/50 dark:bg-blue-900/10 rounded border border-blue-100 dark:border-blue-900/30 font-mono" text={<>Example: "{"{{1}}"} is your verification code"</>} />
                                </Card>
                            )}

                            {/* Header */}
                            {!isAuth && (
                                <Card>
                                    <CardHeader className="flex flex-row items-center gap-4 py-4">
                                        <div className="w-10 h-10 rounded-sm bg-purple-50 dark:bg-purple-600/30 flex items-center justify-center shrink-0">
                                            <LayoutTemplate className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                                                        Header
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="text-gray-400 dark:text-gray-300 cursor-help"><Info size="14" /></span>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-[300px] text-center">
                                                                <p>Header is optional for TEXT, but required for media (IMAGE, VIDEO, DOCUMENT)</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </h2>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Select the type of header for your template.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Header Type Selection Grid */}
                                        <div className={cn("grid gap-3", isSuperAdmin ? "grid-cols-1" : "grid-cols-2 md:grid-cols-4")}>
                                            {headerTypeOptions.map((item) => (
                                                <button
                                                    key={item.type}
                                                    type="button"
                                                    disabled={uploadingMedia}
                                                    onClick={() => {
                                                        if (uploadingMedia || values.header.type === item.type) return;
                                                        setFieldValue("header", getHeaderStateForType(item.type as HeaderType));
                                                    }}
                                                    className={cn(
                                                        "flex flex-row items-center p-3 rounded-sm border-2 transition-all duration-300 text-left gap-4 group relative overflow-hidden h-20",
                                                        uploadingMedia ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
                                                        values.header.type === item.type
                                                            ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 shadow-md"
                                                            : "border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-blue-200 dark:hover:border-blue-800 hover:scale-[1.01]"
                                                    )}
                                                >
                                                    <div className={cn(
                                                        "w-10 h-10 shrink-0 rounded-sm flex items-center justify-center transition-colors",
                                                        values.header.type === item.type ? "bg-blue-500 text-white shadow-lg shadow-blue-500/20" : "bg-gray-100 dark:bg-gray-800 text-gray-500 group-hover:text-blue-500"
                                                    )}>
                                                        <item.icon className="w-6 h-6" />
                                                    </div>
                                                    <div className="space-y-1 min-w-0">
                                                        <p className={cn(
                                                            "text-[12px] font-black uppercase tracking-wider truncate",
                                                            values.header.type === item.type ? "text-blue-600 dark:text-blue-400" : "text-gray-900 dark:text-gray-100"
                                                        )}>{item.title}</p>
                                                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 leading-tight uppercase tracking-tighter truncate">{item.sub}</p>
                                                    </div>
                                                    {values.header.type === item.type && (
                                                        <div className="absolute top-2 right-2">
                                                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
                                                                <Check className="w-3 h-3 text-white" />
                                                            </div>
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="w-full h-px bg-gray-100 dark:bg-gray-700" />
                                        {values.header.type === "TEXT" ? (
                                            <div className="space-y-1">
                                                <div className="flex items-start gap-1.5">
                                                    <div className="flex-1">
                                                        <Input name="header.text" placeholder="Header text (max 60 chars)"
                                                            value={values.header.text}
                                                            // onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("header.text", e.target.value.replace(/[!@#$%^&*()+=[\];':"\\|,.<>/?`~]/g, "").replace(/\p{Emoji_Presentation}/gu, ""))}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const noEmojis = e.target.value.replace(emojiRegex(), "");
                                                                setFieldValue("header.text", noEmojis);
                                                            }}
                                                            onBlur={() => setFieldTouched("header.text", true)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } }}
                                                            error={!!firstErrFor("header.text")}
                                                        />
                                                        <div className="flex items-center justify-between mt-1">
                                                            <div >
                                                                {extractVars(values.header.text).length > 0 ? (
                                                                    <Hint text="1 variable used (max 1) · No emojis allowed" variant="info" className="mt-0" />
                                                                ) : (
                                                                    <Hint text="Lowercase letters, numbers, underscores, spaces · No emojis or special chars" className="mt-0" />
                                                                )}
                                                            </div>
                                                            <CharCount text={values.header.text} max={60} />
                                                        </div>
                                                    </div>
                                                    <div className="shrink-0">
                                                        {(extractVars(values.header.text).length === 0 && extractNamedVars(values.header.text).length === 0) ? (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setFieldValue(
                                                                        "header.text",
                                                                        values.header.text + (values.variableType === "positional" ? "{{1}}" : "{{name}}")
                                                                    )
                                                                }
                                                                className="py-2.5 px-3 rounded-sm bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 text-xs font-bold uppercase tracking-tight hover:bg-emerald-500/20 transition-all whitespace-nowrap"
                                                            >
                                                                + Add Variable
                                                            </button>
                                                        ) : (
                                                            <div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <ErrList errs={errsFor("header.text")} />
                                                </div>

                                                {((values.variableType === "positional" && extractVars(values.header.text).length > 0) || (values.variableType === "named" && extractNamedVars(values.header.text).length > 0)) && (
                                                    <div className="mt-4 p-4 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30">
                                                        <Label className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3 block">
                                                            Header Variable Example{" "}
                                                            <span className="text-red-500">*</span>
                                                        </Label>
                                                        <div className="flex items-center gap-3">
                                                            <Input
                                                                value={values.header.example || ""}
                                                                onChange={(e) => setFieldValue("header.example", e.target.value)}
                                                                onBlur={() => setFieldTouched("header.example", true)}
                                                                placeholder={`Example value for ${values.variableType === "positional" ? "{{1}}" : "{{variable_name}}"}`}
                                                                className="flex-1"
                                                                error={!values.header.example?.trim() && !!firstErrFor("header.example")}
                                                            />
                                                        </div>
                                                        <ErrList errs={errsFor("header.example")} />
                                                    </div>
                                                )}
                                            </div>
                                        ) : values.header.type === "LOCATION" ? (
                                            <div className="p-3 rounded bg-gray-100 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400">
                                                📍 Location coordinates provided during message send
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Label className="text-[12px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest">
                                                        {values.header.type} Content <span className="text-red-500">*</span>
                                                    </Label>
                                                    <div className="flex items-center gap-2">
                                                        {values.header.mediaPreview && (
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveMedia}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-red-50 text-red-600 border border-red-100 text-[10px] font-bold uppercase tracking-tighter hover:bg-red-100 transition-colors"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <div
                                                    onClick={() => !uploadingMedia && fileInputRef.current?.click()}
                                                    className={cn(
                                                        "relative group cursor-pointer overflow-hidden rounded-xl border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center p-8 text-center",
                                                        values.header.mediaPreview ? "border-blue-500/30 bg-blue-50/5 shadow-sm" : "border-gray-200 dark:border-gray-700 dark:bg-gray-700/40 hover:border-blue-500/50 hover:bg-blue-500/5",
                                                        uploadingMedia && "pointer-events-none opacity-80"
                                                    )}
                                                >
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        accept={MEDIA_CONFIG[values.header.type]?.accept || ""}
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            const config = MEDIA_CONFIG[values.header.type];
                                                            const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
                                                            if (config && !config.extensions.includes(fileExt)) {
                                                                toast.error(`Invalid file format. Supported: ${config.extensions.join(", ").toUpperCase()}`);
                                                                return;
                                                            }
                                                            if (config && file.size > config.maxSize) {
                                                                toast.error(`${values.header.type} must be less than ${config.maxSize / (1024 * 1024)}MB`);
                                                                return;
                                                            }
                                                            handleImageUpload(file);
                                                        }}
                                                        className="hidden"
                                                    />

                                                    {uploadingMedia ? (
                                                        <div className="flex flex-col items-center gap-3 py-2">
                                                            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Uploading your {values.header.type.toLowerCase()}...</p>
                                                        </div>
                                                    ) : values.header.mediaPreview ? (
                                                        <div className="w-full flex flex-col items-center gap-4">
                                                            {values.header.type === "IMAGE" ? (
                                                                <div className="relative w-32 h-32 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden bg-white dark:bg-gray-900 translate-y-0 group-hover:-translate-y-1 transition-transform duration-300">
                                                                    <img src={values.header.mediaPreview} alt="Preview" className="w-full h-full object-cover" />

                                                                    <CloudUpload className="w-6 h-6 text-white" />
                                                                </div>
                                                            ) : values.header.type === "VIDEO" ? (
                                                                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                                                    <video src={values.header.mediaPreview} className="w-full h-full object-cover" />
                                                                </div>
                                                            ) : values.header.type === "DOCUMENT" ? (
                                                                <div className="w-max flex flex-col items-center justify-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 transition-all duration-300">
                                                                    <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                                                        <FileText className="w-6 h-6 text-blue-500" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <p className="text-sm font-bold text-gray-900 dark:text-gray-100 cursor-help">
                                                                                    {truncateFileName(values.header.fileName || "Document.pdf", 35)}
                                                                                </p>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent side="top" className="max-w-md break-all">
                                                                                {values.header.fileName || "Document.pdf"}
                                                                            </TooltipContent>
                                                                        </Tooltip>
                                                                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                                            {values.header.fileSize || "0 KB"} • {values.header.fileName?.split('.').pop()?.toUpperCase() || "PDF"}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                                                    <CloudUpload className="w-8 h-8 text-gray-600" />
                                                                </div>
                                                            )}
                                                            <div className="space-y-1">
                                                                <p className="text-sm font-bold text-gray-600 dark:text-gray-500 flex items-center justify-center gap-1.5">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-pulse" />
                                                                    {values.header.type} Loaded
                                                                </p>
                                                                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-semibold">Click to replace file</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="pb-4">
                                                                <CloudUpload className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                                                    Click to upload <span className="text-blue-600 underline underline-offset-4 decoration-blue-500/30">header {values.header.type.toLowerCase()}</span>
                                                                </p>
                                                                <div className="flex flex-col items-center gap-3">
                                                                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-wider uppercase">
                                                                        LIMITS: {MEDIA_CONFIG[values.header.type]?.maxSize / (1024 * 1024)}MB · {MEDIA_CONFIG[values.header.type]?.extensions.join(", ").toUpperCase()}
                                                                    </p>
                                                                    <div className="flex items-center gap-3 w-full max-w-[200px]">
                                                                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                                                        <span className="text-[10px] font-bold text-gray-400 uppercase">OR</span>
                                                                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setShowMediaLibrary(true);
                                                                        }}
                                                                        className="flex items-center gap-2 px-4 py-2 rounded-sm bg-white dark:bg-gray-800 text-blue-600 border border-blue-200 dark:border-blue-800 shadow-sm hover:shadow-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-xs font-bold uppercase tracking-wide"
                                                                    >
                                                                        <LayoutTemplate className="w-4 h-4" />
                                                                        Select from Library
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                <ErrList errs={errsFor("header.mediaExample")} />
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Body */}
                            <Card>
                                <CardHeader className="flex flex-row items-center gap-4 py-4">
                                    <div className="w-10 h-10 rounded-sm bg-emerald-50 dark:bg-emerald-600/30 flex items-center justify-center shrink-0">
                                        <AlignLeft className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Body <span className="text-red-500">*</span></h2>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Write the main content of your message.</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {isAuth ? (
                                        <div className="space-y-4">
                                            <div className="p-3 rounded-lg bg-blue-950/20 border border-blue-900/35">
                                                <p className="text-xs text-blue-400 font-semibold mb-2">Fixed Body for Authentication</p>
                                                <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-gray-100 break-words">
                                                    {values.body.text}
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-2 cursor-pointer group">
                                                    <input type="checkbox" checked={values.footer.addSecurityRecommendation || false}
                                                        id="addSecurityRecommendation"
                                                        onChange={(e) => setFieldValue("footer.addSecurityRecommendation", e.target.checked)}
                                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                                    <Label htmlFor="addSecurityRecommendation" className="mb-0 text-gray-700 dark:text-gray-300 cursor-pointer">Add security recommendation</Label>
                                                </div>
                                                <div className="flex items-center gap-2 cursor-pointer group">
                                                    <input type="checkbox" checked={values.footer.codeExpirationMinutes !== undefined}
                                                        id="codeExpirationMinutes"
                                                        onChange={(e) => setFieldValue("footer.codeExpirationMinutes", e.target.checked ? 5 : undefined)}
                                                        className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500" />
                                                    <Label htmlFor="codeExpirationMinutes" className="mb-0 text-gray-700 dark:text-gray-300 cursor-pointer">Add expiry time for the code</Label>
                                                </div>
                                                {values.footer.codeExpirationMinutes !== undefined && (
                                                    <div className="ml-6">
                                                        <FormField name="footer.codeExpirationMinutes" type="number"
                                                            value={values.footer.codeExpirationMinutes}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                setFieldValue("footer.codeExpirationMinutes", parseInt(e.target.value) || "")
                                                            }
                                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                                setFieldTouched("footer.codeExpirationMinutes", true);
                                                                setFieldValue("footer.codeExpirationMinutes", parseInt(e.target.value) || 5);
                                                            }}
                                                            min={1} max={90}
                                                            error={firstErrFor("footer.codeExpirationMinutes")}
                                                            className="text-sm w-32 mb-0" />
                                                        <Hint text="Expiry in minutes (1-90)" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <textarea
                                                ref={bodyTextareaRef}
                                                value={values.body.text}
                                                onChange={handleBodyChange}
                                                onKeyUp={(e) => setBodyCaretPos((e.target as HTMLTextAreaElement).selectionStart)}
                                                onClick={(e) => setBodyCaretPos((e.target as HTMLTextAreaElement).selectionStart)}
                                                onBlur={() => setFieldTouched("body.text", true)}
                                                placeholder={`Hello {{${values.variableType === "positional" ? "1" : "name"}}}, your order {{${values.variableType === "positional" ? "2" : "order_id"}}} has been shipped.`}
                                                rows={5}
                                                className={cn(
                                                    "mb-0 w-full px-3 py-2 text-sm border rounded-sm resize-none leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100",
                                                    touched.body?.text && (errors as any)?.body?.text ? "border-red-500 focus:ring-red-500" : ""
                                                )}
                                            />
                                            <div className="flex items-center justify-between gap-4">
                                                {/* All body errors from Yup */}
                                                <ErrList errs={errsFor("body.text")} />
                                                <div className="ms-auto">
                                                    <CharCount text={values.body.text} max={1024} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {!isAuth && (
                                        <div className="flex items-center justify-between gap-2 flex-wrap">

                                            <button onClick={insertBodyVar}
                                                className="text-[10px] px-2 py-1 rounded text-emerald-500 border border-emerald-900/40 font-mono transition-all">
                                                + {"{{"}{values.variableType === "positional" ? "1" : "name"}{"}}"}
                                            </button>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {([{ f: "*", icon: Bold }, { f: "_", icon: Italic }, { f: "~", icon: Strikethrough }, { f: "`", icon: Code }] as { f: string; icon: any }[]).map(({ f, icon: Icon }) => (
                                                    <button key={f} onClick={() => insertFormatting(f)}
                                                        className="text-[10px] p-1 rounded text-slate-500 border border-slate-700/30 hover:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"><Icon className="w-3.5 h-3.5" /></button>
                                                ))}
                                                <div className="relative" ref={emojiPickerRef}>
                                                    <button onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                        className="text-[10px] px-1.5 py-1 rounded text-slate-500 border border-slate-700/30 hover:border-slate-600 transition-all flex items-center">
                                                        <Smile className="w-3.5 h-3.5" />
                                                    </button>
                                                    {showEmojiPicker && (
                                                        <div className="absolute z-50 mt-1 right-0">
                                                            <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} previewConfig={{ showPreview: false }} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                        </div>
                                    )}

                                    <div className="flex gap-3 mt-0.5">
                                        {extractVars(values.body.text).length > 0 && (
                                            <span className={cn("text-[9px]", extractVars(values.body.text).length > 7 ? "text-amber-600" : "text-slate-700")}>
                                                vars: {extractVars(values.body.text).length}{extractVars(values.body.text).length > 7 ? " ⚠ >7 recommended max" : ""}
                                            </span>
                                        )}
                                        {extractNamedVars(values.body.text).length > 0 && values.variableType === "named" && (
                                            <span className={cn("text-[9px]", extractNamedVars(values.body.text).length > 7 ? "text-amber-600" : "text-slate-700")}>
                                                vars: {extractNamedVars(values.body.text).length}{extractNamedVars(values.body.text).length > 7 ? " ⚠ >7 recommended max" : ""}
                                            </span>
                                        )}
                                    </div>
                                    {/* Variable examples - Positional */}
                                    {!isAuth && extractVars(values.body.text).length > 0 && values.variableType === "positional" && (
                                        <div className="mt-2.5 p-3 rounded-xl bg-blue-50 dark:bg-blue-950/25 border border-blue-900/35">
                                            <p className="text-[9px] text-blue-400 font-bold uppercase tracking-widest mb-2">
                                                Variable Examples{" "}
                                                <span className="text-red-500">* </span>
                                            </p>
                                            <div className="space-y-1.5">
                                                {extractVars(values.body.text).map((v) => {
                                                    const missing = !values.body.examples?.[v - 1]?.trim();
                                                    return (
                                                        <div key={v} className="flex items-center gap-2">
                                                            <Input
                                                                value={values.body.examples?.[v - 1] || ""}
                                                                onChange={(e) => {
                                                                    const ex = [...(values.body.examples || [])];
                                                                    ex[v - 1] = e.target.value;
                                                                    setFieldValue("body.examples", ex);
                                                                }}
                                                                onBlur={() => setFieldTouched("body.examples", true)}
                                                                placeholder={`Example for {{${v}}}`}
                                                                className="flex-1"
                                                                error={missing && !!firstErrFor("body.examples")}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <ErrList errs={errsFor("body.examples")} />
                                        </div>
                                    )}

                                    {/* Variable examples - Named */}
                                    {!isAuth && extractNamedVars(values.body.text).length > 0 && values.variableType === "named" && (
                                        <div className="mt-2.5 p-3 rounded-xl bg-purple-50 dark:bg-purple-950/25 border border-purple-900/35">
                                            <p className="text-[9px] text-purple-400 font-bold uppercase tracking-widest mb-2">
                                                Named Variable Examples{" "}
                                                <span className="text-red-500">* </span>
                                            </p>
                                            <div className="space-y-1.5">
                                                {extractNamedVars(values.body.text).map((varName, index) => {
                                                    const missing = !values.body.examples?.[index]?.trim();
                                                    return (
                                                        <div key={varName} className="flex items-center gap-2">
                                                            <span className="text-[10px] font-mono text-purple-500 w-32 shrink-0 truncate" title={varName}>
                                                                {"{{" + varName + "}}"}
                                                            </span>
                                                            <Input
                                                                value={values.body.examples?.[index] || ""}
                                                                onChange={(e) => {
                                                                    const ex = [...(values.body.examples || [])];
                                                                    ex[index] = e.target.value;
                                                                    setFieldValue("body.examples", ex);
                                                                }}
                                                                onBlur={() => setFieldTouched("body.examples", true)}
                                                                placeholder={`Example for {{${varName}}}`}
                                                                className="flex-1"
                                                                error={missing && !!firstErrFor("body.examples")}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <ErrList errs={errsFor("body.examples")} />
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Footer */}
                            {!isAuth && (
                                <Card>
                                    <CardHeader className="flex flex-row items-center gap-4 py-4">
                                        <div className="w-10 h-10 rounded-sm bg-gray-100 dark:bg-gray-600/30 flex items-center justify-center shrink-0">
                                            <Type className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Footer <span className="text-xs font-normal text-gray-500">(Optional)</span></h2>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Add extra details or regulatory text at the bottom.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <FormField name="footer.text" placeholder="e.g. Stop to unsubscribe."
                                            value={values.footer.text}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("footer.text", e.target.value)}
                                            onBlur={() => setFieldTouched("footer.text", true)}
                                            error={firstErrFor("footer.text")}
                                        />
                                        <div className="flex items-start justify-between -mt-3 gap-2">
                                            <div className="flex flex-col gap-1">
                                                <ErrList errs={errsFor("footer.text").slice(1)} />
                                                <Hint text="No variables · no URLs · no emojis · max 60" className="mt-0.5" />
                                            </div>
                                            <CharCount text={values.footer.text} max={60} />
                                        </div>

                                    </CardContent>
                                </Card>
                            )}

                            {/* Buttons */}
                            {!isAuth && (
                                <Card>
                                    <CardHeader className="flex items-center justify-between gap-4 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-sm bg-orange-50 dark:bg-orange-600/30 flex items-center justify-center shrink-0">
                                                <MousePointer2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Buttons <span className="text-xs font-normal text-gray-500">(Optional)</span></h2>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Add interactive buttons to your template.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="relative flex justify-end">
                                            <AddButtonDropdown
                                                availableTypes={[
                                                    { value: "quick_reply", label: "Custom", sublabel: "Quick reply button", icon: MessageSquare, maxCount: 10, disabled: values.buttons.filter((b) => b.type === "quick_reply").length >= 10, disabledReason: "Max 10 quick replies reached" },
                                                    { value: "url", label: "Visit website", sublabel: "Open a URL", icon: Link, maxCount: 2, disabled: values.buttons.filter((b) => b.type === "url").length >= 2, disabledReason: "Max 2 URL buttons reached" },
                                                    {
                                                        value: "phone_number",
                                                        label: "Call Phone Number",
                                                        sublabel: "Initiate a phone call",
                                                        icon: Phone,
                                                        maxCount: 1,
                                                        disabled: values.buttons.some((b) => b.type === "phone_number" || b.type === "voice_call"),
                                                        disabledReason: values.buttons.some((b) => b.type === "voice_call")
                                                            ? "Cannot use with Voice Call button"
                                                            : "Max 1 phone button reached"
                                                    },
                                                    // { value: "flow", label: "Complete flow", sublabel: "Open a WhatsApp Flow", icon: Workflow, maxCount: 1, disabled: values.buttons.filter((b) => b.type === "flow").length >= 1, disabledReason: "Max 1 flow button reached" },
                                                    { value: "copy_code", label: "Copy offer code", sublabel: "Copy a promo code", icon: Copy, maxCount: 1, disabled: values.buttons.filter((b) => b.type === "copy_code").length >= 1, disabledReason: "Max 1 copy code button" },
                                                    {
                                                        value: "voice_call",
                                                        label: "Voice Call",
                                                        sublabel: "Call via WhatsApp",
                                                        icon: Phone,
                                                        maxCount: 1,
                                                        disabled: values.buttons.some((b) => b.type === "voice_call" || b.type === "phone_number"),
                                                        disabledReason: values.buttons.some((b) => b.type === "phone_number")
                                                            ? "Cannot use with Phone button"
                                                            : "Max 1 voice call button"
                                                    },
                                                ]}
                                                onSelect={(type) => addButton(type as ButtonType)}
                                                disabled={values.buttons.length >= 10}
                                            />
                                        </div>
                                    </CardHeader>
                                    <CardContent className="p-6 space-y-3">
                                        {groupOrder.map((group, groupIdx) => (
                                            <Fragment key={group}>
                                                {group === 'quick_reply' ? (
                                                    /* Quick Reply Box */
                                                    <div
                                                        className={cn(
                                                            "space-y-3 p-4 border border-gray-200 dark:border-gray-700 rounded-xl transition-all",
                                                            draggedGroup === 'quick_reply' && "opacity-50 scale-[0.98] ring-2 ring-blue-400 shadow-lg",
                                                            dragOverGroup === 'quick_reply' && "ring-2 ring-orange-400 border-orange-300 bg-orange-50/30 dark:bg-orange-900/10"
                                                        )}
                                                        draggable
                                                        onDragStart={(e) => handleGroupDragStart(e, 'quick_reply')}
                                                        onDragOver={(e) => handleGroupDragOver(e, 'quick_reply')}
                                                        onDrop={(e) => handleGroupDrop(e, 'quick_reply')}
                                                        onDragEnd={() => { setDraggedGroup(null); setDragOverGroup(null); }}
                                                    >
                                                        <div className="flex items-center justify-between pb-1">
                                                            <div className="flex items-center gap-2">
                                                                <GripHorizontal size={14} className="text-gray-400 cursor-grab active:cursor-grabbing mr-1" />
                                                                <h3 className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">Quick Reply Buttons</h3>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-3">
                                                            {values.buttons.map((btn, i) => ({ btn, i })).filter(x => x.btn.type === 'quick_reply').map(({ btn, i }) => {
                                                                const qrGroup = values.buttons.filter(b => b.type === 'quick_reply');
                                                                const isDraggable = qrGroup.length >= 2;
                                                                return (
                                                                    <ButtonEditor
                                                                        key={btn.id}
                                                                        btn={btn}
                                                                        index={i}
                                                                        totalCount={values.buttons.length}
                                                                        errors={errsForBtn(i)}
                                                                        onUpdate={updateButton}
                                                                        onBlur={(field) => setFieldTouched(`buttons.${i}.${field}`, true)}
                                                                        onRemove={removeButton}
                                                                        draggable={isDraggable}
                                                                        onDragStart={handleDragStart}
                                                                        onDragOver={handleDragOver}
                                                                        onDrop={handleDrop}
                                                                        onDragEnd={handleDragEnd}
                                                                        isDragging={draggedIndex === i}
                                                                        isDragOver={dragOverIndex === i}
                                                                    />
                                                                );
                                                            })}
                                                            {values.buttons.filter(b => b.type === 'quick_reply').length === 0 && (
                                                                <div className="py-6 flex flex-col items-center justify-center text-center opacity-40 bg-gray-50/50 dark:bg-gray-950/20 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                                                                    <MessageSquare className="w-6 h-6 text-gray-400 mb-1" />
                                                                    <p className="text-xs font-medium text-gray-400">No quick replies added</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    /* CTA Box */
                                                    <div
                                                        className={cn(
                                                            "space-y-3 p-4 border border-gray-200 dark:border-gray-700 rounded-xl transition-all",
                                                            draggedGroup === 'cta' && "opacity-50 scale-[0.98] ring-2 ring-blue-400 shadow-lg",
                                                            dragOverGroup === 'cta' && "ring-2 ring-orange-400 border-orange-300 bg-orange-50/30 dark:bg-orange-900/10"
                                                        )}
                                                        draggable
                                                        onDragStart={(e) => handleGroupDragStart(e, 'cta')}
                                                        onDragOver={(e) => handleGroupDragOver(e, 'cta')}
                                                        onDrop={(e) => handleGroupDrop(e, 'cta')}
                                                        onDragEnd={() => { setDraggedGroup(null); setDragOverGroup(null); }}
                                                    >
                                                        <div className="flex items-center justify-between pb-1">
                                                            <div className="flex items-center gap-2">
                                                                <GripHorizontal size={14} className="text-gray-400 cursor-grab active:cursor-grabbing mr-1" />
                                                                <h3 className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">Call to Action Buttons</h3>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-3">
                                                            {values.buttons.map((btn, i) => ({ btn, i })).filter(x => x.btn.type !== 'quick_reply').map(({ btn, i }) => {
                                                                const ctaGroup = values.buttons.filter(b => b.type !== 'quick_reply');
                                                                const isDraggable = ctaGroup.length >= 2;
                                                                return (
                                                                    <ButtonEditor
                                                                        key={btn.id}
                                                                        btn={btn}
                                                                        index={i}
                                                                        totalCount={values.buttons.length}
                                                                        errors={errsForBtn(i)}
                                                                        onUpdate={updateButton}
                                                                        onBlur={(field) => setFieldTouched(`buttons.${i}.${field}`, true)}
                                                                        onRemove={removeButton}
                                                                        draggable={isDraggable}
                                                                        onDragStart={handleDragStart}
                                                                        onDragOver={handleDragOver}
                                                                        onDrop={handleDrop}
                                                                        onDragEnd={handleDragEnd}
                                                                        isDragging={draggedIndex === i}
                                                                        isDragOver={dragOverIndex === i}
                                                                    />
                                                                );
                                                            })}
                                                            {values.buttons.filter(b => b.type !== 'quick_reply').length === 0 && (
                                                                <div className="py-6 flex flex-col items-center justify-center text-center opacity-40 bg-gray-50/50 dark:bg-gray-950/20 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                                                                    <MousePointer2 className="w-6 h-6 text-gray-400 mb-1" />
                                                                    <p className="text-xs font-medium text-gray-400">No CTA buttons added</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {groupIdx === 0 && (
                                                    <div className="py-2 flex items-center gap-4 group/divider">
                                                        <div className="h-px border-t border-dashed border-gray-200 dark:border-gray-800 flex-1" />
                                                        <div className="text-[9px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-widest px-2 whitespace-nowrap">
                                                            Drag sections to reorder
                                                        </div>
                                                        <div className="h-px border-t border-dashed border-gray-200 dark:border-gray-800 flex-1" />
                                                    </div>
                                                )}
                                            </Fragment>
                                        ))}
                                        <div>
                                            {lockedType === "otp" && (
                                                <Hint variant="warning" text="OTP mode active — limitations applied" />
                                            )}
                                            <Hint text="Max 10 total buttons across groups." className="mt-4" />
                                            <ErrList errs={arrayBtnErrs()} />
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* ── Preview (Only show if not already shown in side panel) ── */}
                        {!setWhatsAppValues && (
                            <div className="lg:col-span-1">
                                <Card>
                                    <CardHeader className="flex flex-row items-center gap-4 py-4">
                                        <div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 flex items-center justify-center shrink-0">
                                            <Eye className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                        </div>
                                        <div className="flex-1">
                                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Preview</h2>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Live preview of your WhatsApp message.</p>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex justify-center">
                                            <WhatsAppPreview values={values} />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation Buttons — fixed at bottom */}
            <div className="shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] z-10 w-full relative">
                <div className="flex items-center justify-end">

                    <div className="flex gap-4 items-center">
                        {canDraft && (
                            <Button type="button" onClick={handleDraft}
                                disabled={!values.templateName?.trim() || isDrafting || isCreating}
                                variant="secondary"
                                className="bg-green-600 text-white border-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                            >
                                {isDrafting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                {isDrafting ? "Submitting..." : isEditMode ? "Update Draft" : "Save as Draft"}
                            </Button>
                        )}

                        <Button type="button" onClick={handleDirectSubmit}
                            disabled={isSubmitDisabled}
                            variant="default"
                        >
                            {isCreating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            {
                                isCreating
                                    ? "Submitting..."
                                    : isEditMode
                                        ? "Update & Submit" :isSuperAdmin ? "Add to Library"
                                        : "Submit to Meta"
                            }
                        </Button>
                    </div>
                </div>
            </div>

            {showMediaLibrary && (
                <Portal>
                    <MediaLibrary
                        selectedMediaType={values.header.type.toLowerCase() as MediaType}
                        setSelectedMedia={(val: any) => {
                            if (typeof val === 'string') {
                                handleMediaLibrarySelect(val);
                            }
                        }}
                        onClose={() => setShowMediaLibrary(false)}
                    />
                </Portal>
            )}

            <AddIndustryModal
                isOpen={isAddIndustryModalOpen}
                onClose={() => setIsAddIndustryModalOpen(false)}
                categoryKey={values.category.toLowerCase() || 'utility'}
                onSuccessAdd={(industryId) => setLibraryMeta(prev => ({ ...prev, templateLibraryIndustryId: industryId, templateLibrarySubcategoryId: "" }))}
            />

            <AddSubcategoryModal
                isOpen={isAddSubcategoryModalOpen}
                onClose={() => setIsAddSubcategoryModalOpen(false)}
                industryId={libraryMeta.templateLibraryIndustryId}
                onSuccessAdd={(subcategoryId) => setLibraryMeta(prev => ({ ...prev, templateLibrarySubcategoryId: subcategoryId }))}
            />
        </div>
    );
};

// ─── Initial Values ───────────────────────────────────────────────────────────
const INITIAL_VALUES: FormValues = {
    templateName: "",
    category: "MARKETING",
    language: "en_US",
    variableType: "positional",
    header: { type: "TEXT", text: "", mediaExample: "", mediaPreview: "", example: "", fileName: "", fileSize: "" },
    body: { text: "", examples: [] },
    footer: { text: "", addSecurityRecommendation: false, codeExpirationMinutes: undefined },
    buttons: [],
};


// Helper function to convert template to form values
function convertTemplateToFormValues(template: any): FormValues {
    const components = template.metaPayload?.components || [];

    const headerComp = components.find((c: any) => c.type.toLowerCase() === "header");
    const bodyComp = components.find((c: any) => c.type.toLowerCase() === "body");
    const footerComp = components.find((c: any) => c.type.toLowerCase() === "footer");
    const buttonsComp = components.find((c: any) => c.type.toLowerCase() === "buttons");

    let headerType: HeaderType = "TEXT";
    let headerText = "";
    let headerMediaExample = "";
    let headerMediaPreview = "";
    let headerExample = "";

    if (headerComp) {
        const format = headerComp.format?.toUpperCase();
        if (format === "TEXT") {
            headerType = "TEXT";
            headerText = headerComp.text || "";
            const headerTextExample = headerComp.example?.header_text?.[0];
            if (headerTextExample !== undefined) {
                headerExample = Array.isArray(headerTextExample)
                    ? headerTextExample[0] || ""
                    : headerTextExample || "";
            } else if (headerComp.example?.header_text_named_params) {
                headerExample = headerComp.example.header_text_named_params[0]?.example || "";
            }
        } else if (format === "IMAGE") {
            headerType = "IMAGE";
            headerMediaExample = headerComp.example?.header_handle?.[0] || "";
            headerMediaPreview = headerComp.mediaUrl || "";
        } else if (format === "VIDEO") {
            headerType = "VIDEO";
            headerMediaExample = headerComp.example?.header_handle?.[0] || "";
            headerMediaPreview = headerComp.mediaUrl || "";
        } else if (format === "DOCUMENT") {
            headerType = "DOCUMENT";
            headerMediaExample = headerComp.example?.header_handle?.[0] || "";
            headerMediaPreview = headerComp.mediaUrl || "";
        } else if (format === "LOCATION") {
            headerType = "LOCATION";
        }
    }

    const bodyText = bodyComp?.text || getAuthBodyText(template.language || template.metaPayload?.language || "en", bodyComp?.add_security_recommendation ?? false);
    let bodyExamples: string[] = [];

    if (bodyComp?.example?.body_text?.[0]) {
        bodyExamples = bodyComp.example.body_text[0];
    } else if (bodyComp?.example?.body_text_named_params) {
        const namedParams = bodyComp.example.body_text_named_params;
        const paramsArray = Array.isArray(namedParams[0]) ? namedParams[0] : namedParams;
        bodyExamples = paramsArray?.map((p: any) => p?.example || "") || [];
    }

    const footerText = footerComp?.text || "";

    const buttons: ButtonItem[] = [];
    if (buttonsComp?.buttons) {
        buttonsComp.buttons.forEach((btn: any, index: number) => {
            const btnType = normalizeButtonType(btn.type);
            const buttonItem: ButtonItem = {
                id: index,
                type: btnType,
                text: btn.text || "",
            };
            if (btnType === "url") {
                buttonItem.url = btn.url;
                buttonItem.urlExample = btn.example?.[0] || "";
            } else if (btnType === "phone_number") {
                buttonItem.phone = btn.phone_number;
            } else if (btnType === "copy_code") {
                buttonItem.copyCodeExample = btn.example || "";
            } else if (btnType === "otp") {
                buttonItem.otpType = btn.otp_type || "copy_code";
            } else if (btnType === "flow") {
                buttonItem.flowId = btn.flow_id;
            } else if (btnType === "voice_call") {
                buttonItem.voiceCallDisplayText = btn.text || "Call on WhatsApp";
            }
            buttons.push(buttonItem);
        });
    }

    if (template.mediaUrl) {
        headerMediaPreview = template.mediaUrl;
    }

    return {
        templateName: template.metaTemplateName || template.name || template.label || "",
        category: ((template.category) || (template.categoryKey) || "UTILITY").toUpperCase() as Category,
        language: template.language || template.metaPayload?.language || "en_US",
        variableType: inferVariableTypeFromTemplate(template),
        header: {
            type: headerType,
            text: headerText,
            mediaExample: headerMediaExample,
            mediaPreview: headerMediaPreview,
            example: headerExample,
            fileName: headerMediaPreview ? headerMediaPreview.split("/").pop() || "Media Content" : "",
            fileSize: headerMediaPreview ? "Internal Library" : ""
        },

        body: {
            text: bodyText,
            examples: bodyExamples,
        },
        footer: {
            text: footerText,
            addSecurityRecommendation: bodyComp?.add_security_recommendation ?? false,
            codeExpirationMinutes: footerComp?.code_expiration_minutes ?? undefined,
        },
        buttons: buttons,
    };
}

// Using `validate` (not `validationSchema`) so we can pass the full `values`
// as Yup context — this lets every .test() read sibling fields via
// `this.options.context` (e.g. body.text reads `category` and `variableType`).
export default function WhatsAppTemplateBuilder() {
    const { id: chatBotId, templateId } = useParams();
    const location = useLocation();
    const schema = useMemo(() => buildSchema(), []);

    // ── Unsaved changes state — lifted here so hooks are at component level ──
    const [userHasEdited, setUserHasEdited] = useState(false);
    const [isSubmittingSuccessfully, setIsSubmittingSuccessfully] = useState(false);
    const isSubmittingSuccessfullyRef = useRef(false);
    const { data: templateData, isFetching } = useTemplate(
        templateId || null,
        { enabled: Boolean(templateId) }
    );
  const { data: allTemplatesResponse } = useTemplates(
        chatBotId || null,
        { page: 1, limit: 100, sortOrder: 'asc' },
        { enabled: Boolean(chatBotId) }
    );
  const isLibraryTemplate = Boolean(location.state?.templateData && !templateId);
  const existingTemplateNames = useMemo(() => {
        const templates = allTemplatesResponse?.data?.templates || [];
        return new Set(
            templates
                .filter((template) => template._id !== templateId)
                .flatMap((template) => [template.metaTemplateName, template.name])
                .filter(Boolean)
                .map((name) => name!.toLowerCase())
        );
    }, [allTemplatesResponse?.data?.templates, templateId]);

    // ── useBlocker at top level (not inside render prop) ──────────────────
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            userHasEdited &&
            !isSubmittingSuccessfullyRef.current &&  // ← read ref, not state
            currentLocation.pathname !== nextLocation.pathname
    );

    // ── Tab close / refresh guard ─────────────────────────────────────────
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!userHasEdited || isSubmittingSuccessfully) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [userHasEdited, isSubmittingSuccessfully]);

    const initialValues = useMemo(() => {
        if (isFetching) return { ...INITIAL_VALUES };
        if (Boolean(templateId) && templateData) {
            return convertTemplateToFormValues(templateData?.data);
        }
        if (location.state?.templateData) {
            return convertTemplateToFormValues(location.state.templateData);
        }
        return { ...INITIAL_VALUES };
    }, [templateId, templateData, chatBotId, isFetching, location.state]);

    // Reset local edit tracking when the URL/context changes (e.g., switching bots)
    useEffect(() => {
        setUserHasEdited(false);
        if (isSubmittingSuccessfullyRef.current) {
            isSubmittingSuccessfullyRef.current = false;
        }
    }, [chatBotId, templateId]);

    const isTemplateNameDisabled = Boolean(templateId) && templateData && (
        templateData?.data.status === 'approved' ||
        (templateData?.data.status === 'submitted' && templateData?.data.metaStatus === 'APPROVED')
    ) as boolean;

    const validate = useCallback(async (values: FormValues) => {
        try {
            await schema.validate(values, {
                abortEarly: false,
                context: {
                    ...values,
                    existingTemplateNames,
                },
            });
            return {};
        } catch (err) {
            if (!(err instanceof Yup.ValidationError)) return {};
            const nested: Record<string, any> = {};
            err.inner.forEach((e) => {
                if (!e.path) return;
                const parts = e.path.replace(/\[(\d+)\]/g, ".$1").split(".");
                let cur = nested;
                parts.forEach((part, i) => {
                    if (i === parts.length - 1) {
                        if (cur[part] === undefined) cur[part] = e.message;
                    } else {
                        const nextPart = parts[i + 1];
                        const nextIsIndex = /^\d+$/.test(nextPart);
                        if (cur[part] === undefined) cur[part] = nextIsIndex ? [] : {};
                        cur = cur[part];
                    }
                });
            });
            return nested;
        }
    }, [schema]);

    if (templateId && isFetching && !templateData) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 animate-pulse">Loading template...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* ── Unsaved Changes Modal — reuses your existing Modal component ── */}
            <Modal
                isOpen={blocker.state === "blocked"}
                onClose={() => blocker.reset?.()}
                title="Leave this page?"
                size="sm"
                showFooter={false}
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg">
                        <TriangleAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                            You have unsaved changes. If you leave now, your template
                            changes will be permanently lost.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => blocker.reset?.()}
                        >
                            Stay on Page
                        </Button>
                        <Button
                            type="button"
                            className="flex-1 bg-red-600 hover:bg-red-700 !text-white border-0"
                            onClick={() => blocker.proceed?.()}
                        >
                            Leave Anyway
                        </Button>
                    </div>
                </div>
            </Modal>

            <Formik<FormValues>
                key={templateId || "create"}
                initialValues={initialValues}
                validate={validate}
                validateOnChange
                enableReinitialize
                validateOnBlur
                validateOnMount={isLibraryTemplate}
                initialTouched={isLibraryTemplate ? { templateName: true } : undefined}
                onSubmit={(_values, helpers) => { helpers.setSubmitting(false); }}
            >
                {() => (
                    <Form>
                        <TemplateForm
                            canDraft={!templateId || (templateData?.data.status !== 'approved' && templateData?.data.metaStatus !== 'APPROVED')}
                            chatBotId={chatBotId!}
                            isTemplateNameDisabled={isTemplateNameDisabled!}
                            templateId={templateId}
                            onUserEdited={() => setUserHasEdited(true)}
                            onSubmitSuccess={() => {
                                isSubmittingSuccessfullyRef.current = true;  // ← sync, read immediately by blocker
                                setIsSubmittingSuccessfully(true);            // ← keep state for beforeunload guard
                                setUserHasEdited(false);
                            }}
                            isEditMode={Boolean(templateId)}
                            isLibraryTemplate={isLibraryTemplate}
                            templateData={templateData}
                        />
                    </Form>
                )}
            </Formik>
        </>
    );
}
